/* ================================================================
   regions/au/content.ts — all site copy & data for the Australian site.
   Edit text here; components import it through src/config/content.ts,
   which serves whichever market the build was made for.

   Facts about the market (contact details, URLs, currency) come from
   ./region.json, and prices are written with money() so the currency
   symbol is never typed by hand. The prose itself is Australian and
   stays here: another market brings its own content pack.
   ================================================================ */

import region from './region.json';
import { formatMoney, currencySymbol, phoneHref, RegionProfile } from '../../market';
import type { CalcProfession, CalcRow, MarketNotice } from '../../contentTypes';

const REGION: RegionProfile = region;

/** A price in the market's currency: money(59) → "A$59", money(0.1, 2) → "A$0.10". */
const money = (amount: number, decimals = 0) => formatMoney(REGION, amount, decimals);
const APP_URL = REGION.apps.juristab;
const DOCUDIVE_URL = REGION.apps.docudive;
/** Shown before a plan's price, e.g. "$" — the hero says which dollars. */
const CURRENCY_SYMBOL = currencySymbol(REGION);

/* ── Shared / Global ── */

export const SITE = {
  name: REGION.site.name,
  tagline: 'Law made readable and clear.',
  copyright: '\u00A9 2025 JurisTab. All rights reserved.',
  appUrl: APP_URL,
  docuDiveUrl: DOCUDIVE_URL,
};

export const CONTACT_INFO = {
  email: REGION.contact.email,
  emailHref: `mailto:${REGION.contact.email}`,
  phone: REGION.contact.phone,
  phoneHref: phoneHref(REGION.contact.phone),
  location: REGION.contact.location,
};

export const SOCIAL_LINKS = REGION.social;

/* ── Navbar ── */

export const NAV_ITEMS = [
  { label: 'Home', href: '/' },
  { label: 'Pricing', href: '/pricing' },
  {
    label: 'Resources',
    dropdown: [
      { label: 'About Us', desc: 'Our mission & team', href: '/about' },
      { label: 'Case Management', desc: 'Matters, time, billing & e-signatures', href: '/case-management' },
      { label: 'Legal Research', desc: 'AI case law research capabilities', href: '/features' },
      { label: 'Workflow', desc: 'Document automation & contract review', href: '/workflow-features' },
      { label: 'Practice Areas', desc: 'AI research by legal domain', href: '/practice-areas' },
      { label: 'Blogs', desc: 'Insights & updates', href: '/blogs' },
    ],
  },
  {
    label: 'Data Privacy & Governance',
    dropdown: [
      { label: 'Responsible AI', desc: 'Ethics, principles & the 5P governance framework', href: '/responsible-ai' },
      { label: 'Data Privacy', desc: 'How we secure client data on AWS', href: '/data-privacy' },
    ],
  },
  {
    label: 'Tools & Calculators',
    dropdown: [
      { label: 'In-House Lawyers', desc: 'ROI for corporate legal teams', href: '/calculators/in-house' },
      { label: 'Private Practice', desc: 'ROI on billable capacity', href: '/calculators/private-practice' },
      { label: 'Conveyancers', desc: 'ROI on matter throughput', href: '/calculators/conveyancers' },
    ],
  },
  {
    label: 'Products',
    dropdown: [
      { label: 'JurisTab', desc: 'AI legal research', href: APP_URL, external: true },
      { label: 'DocuDive', desc: 'AI document analysis', href: DOCUDIVE_URL, external: true },
    ],
  },
  { label: 'Contact', href: '/contact' },
];

/* ── Home page ── */

export const HOME = {
  hero: {
    badge: 'AI-Powered \u00B7 Case Management, Workflow & Legal Research',
    title: 'Empowering Legal\nProfessionals with\n',
    titleAccent: 'AI Solutions',
    subtitle:
      'Run your matters end to end \u2014 client intake, calendars, meetings, time, billing and e-signatures \u2014 then let AI do the document work on the same files: contract review and redlining, risk reports, court forms, and dedicated suites for family, criminal and property practice. Research Australian case law and legislation without leaving the matter.',
    ctaPrimary: 'Explore JurisTab \u2192',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Watch a demo',
    ctaSecondaryLink: '/contact',
    pills: [
      { icon: '\u{1F4BC}', text: 'Case & matter management' },
      { icon: '\u{1F9E9}', text: 'Document workflow automation' },
      { icon: '\u{1F50D}', text: 'AI-powered legal research' },
    ],
    stats: [
      { value: '600K+', label: 'Cases indexed' },
      { value: '100K+', label: 'Legislation records' },
      { value: '44', label: 'AI workflow services' },
    ],
  },

  loginCard: {
    signUp: {
      title: 'Get started free',
      subtitle: 'Enter your email to create an account.',
      button: 'Continue with Email',
      footerText: 'Already have an account?',
      footerAction: 'Sign in \u2192',
    },
    signIn: {
      title: 'Welcome back',
      subtitle: 'Sign in to your JurisTab workspace.',
      button: 'Sign In',
      footerText: "Don\u2019t have an account?",
      footerAction: 'Sign up \u2192',
    },
    emailLabel: 'Email',
    emailPlaceholder: 'you@chambers.com.au',
    passwordLabel: 'Password',
    passwordPlaceholder: '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022',
  },

  /* The three modules the product ships. Rendered on the home page as three
     stacked rows — a short pitch on the left, that module's capabilities as
     small icon + label chips on the right. `icon` is a key into PILLAR_ICONS
     in Home.tsx; content.ts stays free of JSX. */
  pillars: {
    tag: 'Platform',
    title: 'Three modules,',
    titleAccent: 'one workspace',
    subtitle:
      'Run the practice, automate the document work, and research the law \u2014 case management, workflow automation and legal research under one login, sharing the same matters, the same files and one bill.',
    rows: [
      {
        index: '01',
        name: 'Case Management',
        tagline: 'Run the practice \u2014 first enquiry to final invoice',
        desc:
          'The matter file itself: clients, deadlines, tasks, meetings, time, documents and bills in one place \u2014 and the same matters the workflow services read from, so nothing is entered twice.',
        link: '/case-management',
        linkText: 'Explore Case Management \u2192',
        items: [
          { icon: 'userPlus',  label: 'Client Intake & CRM',          desc: 'Enquiry to client, conflict-checked' },
          { icon: 'briefcase', label: 'Case & Matter Management',     desc: 'One live file per matter' },
          { icon: 'folder',    label: 'Document & File Management',   desc: 'Every file in one tree' },
          { icon: 'invoice',   label: 'Billing & Invoicing',          desc: 'Draft bill to issued tax invoice' },
          { icon: 'clock',     label: 'Time & Expense Tracking',      desc: 'Live timer and billable share' },
          { icon: 'video',     label: 'Tasks & Online Meetings',      desc: 'Video rooms clients join by link' },
          { icon: 'calendar',  label: 'Legal Calendaring',            desc: 'Limitation dates, synced to Outlook' },
          { icon: 'signature', label: 'Electronic Signatures',        desc: 'Send, sign and store in the file' },
        ],
      },
      {
        index: '02',
        name: 'Workflow Automation',
        tagline: '44 agentic services \u2014 plus dedicated family, criminal and property suites',
        desc:
          'Point a service at the documents already in the matter and get work back \u2014 a redlined contract, a risk register, a completed court form, an affidavit draft \u2014 with sources cited and the exact cost shown before anything runs.',
        link: '/workflow-features',
        linkText: 'See every service \u2192',
        items: [
          { icon: 'checkSquare', label: 'Contract & Disclosure Review', desc: 'Clause by clause, with redline' },
          { icon: 'alert',       label: 'Risk & Issues Report',         desc: 'Every risk found and ranked' },
          { icon: 'columns',     label: 'Clause Comparison',            desc: 'Two drafts, one clean diff' },
          { icon: 'penTool',     label: 'Legal Argument Draft',         desc: 'First drafts on your letterhead' },
          { icon: 'form',        label: 'Prepare Court Form',           desc: 'The court\u2019s own form, filled' },
          { icon: 'counsel',     label: 'Counsel',                      desc: 'Chat across the whole matter' },
          { icon: 'table',       label: 'Document Matrix Review',       desc: 'Many documents, one table' },
          { icon: 'layers',      label: 'Practice-Area Suites',         desc: 'Family, criminal and property' },
        ],
      },
      {
        index: '03',
        name: 'Legal Research',
        tagline: 'Australian case law and legislation, read for you',
        desc:
          'Search 600,000+ Australian judgments and 100,000+ legislation records, then open a case and see how it was actually decided \u2014 without reading the full text to find out.',
        link: '/features',
        linkText: 'Explore Legal Research \u2192',
        items: [
          { icon: 'search',    label: 'Search & Filters',       desc: 'Court, party, date \u2014 always free' },
          { icon: 'unlock',    label: 'Case Unlock',            desc: '600K+ Australian judgments' },
          { icon: 'bookOpen',  label: 'Legislation',            desc: 'The section as it read then' },
          { icon: 'gitBranch', label: 'Reasoning',              desc: 'Issue to final order, mapped' },
          { icon: 'users',     label: 'Appearances',            desc: 'Who argued what, before whom' },
          { icon: 'clipboard', label: 'Brief',                  desc: 'Any judgment, in IRAC form' },
          { icon: 'link',      label: 'JurisLink',              desc: 'Citation lineage at a click' },
          { icon: 'message',   label: 'JurisBot',               desc: 'Ask the case a question' },
        ],
      },
    ],
  },

  /* Dedicated practice-area service suites, plus the cross-practice tools
     that sit beside them. Service names match the app's catalogue
     (workflow-automation config.yaml) exactly. */
  suites: {
    tag: 'Built for Your Practice',
    title: 'Services that know ',
    titleAccent: 'your practice area',
    subtitle:
      'Beyond the general services, three practice areas get their own suite \u2014 built around the documents those matters actually run on. Every line of output links back to the page it came from, and any arithmetic is done in code, not guessed by the model.',
    link: '/practice-areas',
    linkText: 'See every practice area \u2192',
    items: [
      {
        id: 'family-law',
        name: 'Family Law',
        count: '14 services',
        desc: 'Financial disclosure through to consent orders, with the asset pool and every figure traced to its source.',
        services: [
          'Disclosure Cross-Check',
          'Asset Pool Schedule',
          'Affidavit Draft',
          'Consent Orders & Minutes',
          'Family Violence & Risk Screening',
          'Superannuation Splitting Check',
        ],
      },
      {
        id: 'criminal-law',
        name: 'Criminal Law & Defence',
        count: '13 services',
        desc: 'From the brief of evidence to the plea \u2014 set to the offence\u2019s jurisdiction, from any state or territory to Commonwealth.',
        services: [
          'Brief of Evidence Review',
          'Charge Analysis',
          'Witness Inconsistency Check',
          'Evidence Objections Schedule',
          'Bail Application',
          'Plea in Mitigation',
        ],
      },
      {
        id: 'property-real-estate',
        name: 'Property & Real Estate',
        count: '4 services',
        desc: 'Contracts of sale, disclosure statements and title \u2014 reviewed, researched and explained for the client.',
        services: [
          'Property Contract & Disclosure Review',
          'Title & Encumbrance Memo',
          'Property Law Research',
          'Plain-Language Explainer',
        ],
      },
    ],
    tools: [
      {
        icon: 'counsel',
        name: 'Counsel',
        desc: 'Ask questions across up to 50 documents in a matter. JurisBot answers with inline citations and can hand back Word or Excel files on your letterhead.',
      },
      {
        icon: 'form',
        name: 'Court & Tribunal Forms',
        desc: 'A library of official forms from Australian courts, tribunals and land titles offices. Prepare Court Form fills the court\u2019s own form from the matter documents and flags any gaps.',
      },
      {
        icon: 'table',
        name: 'Document Matrix Review',
        badge: 'Beta',
        desc: 'Put many documents against your own questions. Each cell is answered with reasoning and page citations, then exported to Excel.',
      },
    ],
  },

  showcase: {
    tag: 'In Action',
    title: 'See how ',
    titleAccent: 'JurisTab',
    titleSuffix: '\nworks in practice',
    subtitle:
      'One workspace where the matter file, the document work and the case-law research all point at the same client, the same deadlines and the same documents \u2014 so a fact entered once is never entered again.',
    checklist: [
      'Case management \u2014 intake and conflict checks, matters, tasks and online meetings, calendaring, time, invoicing and e-signatures in one file',
      '44 workflow services \u2014 contract review with redline, risk reports, court forms, and dedicated family, criminal and property suites',
      'Counsel \u2014 ask questions across up to 50 matter documents and get cited answers, Word and Excel files back',
      'Keyword search with detailed filters across 600K+ Australian cases and 100K+ legislation records',
      'Tabs for Content, Ask JurisBot, Reasoning, Appearances, Brief, and JurisLink \u2014 every case in one view',
    ],
    ctaPrimary: 'Try it now \u2192',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'View all features',
    ctaSecondaryLink: '/features',
    badgeIcon: '\u26A1',
    badgeLabel: 'Average search time',
    badgeValue: '2.4 seconds',
    imgAlt: 'JurisTab product screenshot',
  },

  products: {
    tag: 'Our Products',
    title: 'Two tools, one ',
    titleAccent: 'ecosystem',
    subtitle:
      'JurisTab and DocuDive \u2014 purpose-built AI tools that cover the practice end to end, from running the matter to automating its documents and researching the law behind it.',
    items: [
      {
        label: 'Case Management, Workflow & Legal Research',
        name: 'JurisTab',
        desc: 'Case management for the whole matter \u2014 intake, calendars, meetings, time, billing and e-signatures \u2014 with 44 agentic workflow services and Counsel on the same documents, and AI-powered research across 600,000+ Australian cases with JurisLink, Reasoning, Appearances and Brief.',
        link: APP_URL,
        linkText: 'Open JurisTab \u2192',
        featured: true,
      },
      {
        label: 'Document Analysis',
        name: 'DocuDive',
        desc: 'AI-powered document intelligence for legal professionals. Upload, interrogate, and extract structured insights from contracts, briefs, and legal filings instantly.',
        link: DOCUDIVE_URL,
        linkText: 'Open DocuDive \u2192',
        featured: false,
      },
    ],
  },

  resources: {
    tag: 'Resources',
    title: 'Everything you need\nto ',
    titleAccent: 'get started',
    subtitle:
      'From AI governance documentation to platform guides \u2014 everything your firm needs to adopt JurisTab with confidence.',
    items: [
      {
        icon: '\u{1F3DB}\uFE0F',
        title: 'About JurisTab',
        desc: 'Learn about our mission to empower legal professionals with AI-driven insights and streamlined access to essential legal information.',
        tag: 'ABOUT',
        link: '/about',
      },
      {
        icon: '\u2696\uFE0F',
        title: 'Responsible AI',
        desc: 'Our commitment to ethical AI. Read about the principles, frameworks, and safeguards that govern how JurisTab uses artificial intelligence.',
        tag: 'GOVERNANCE',
        link: '/responsible-ai',
      },
      {
        icon: '\u{1F512}',
        title: 'Data Privacy',
        desc: 'How JurisTab secures client data on AWS \u2014 encryption, access controls, Australian data residency, and zero training on your documents.',
        tag: 'SECURITY',
        link: '/data-privacy',
      },
      {
        icon: '\u{1F4BC}',
        title: 'Case Management',
        desc: 'Running the practice \u2014 client intake and conflict checks, matters and parties, documents, time and expenses, invoicing, calendaring, online meetings, court forms and e-signatures.',
        tag: 'CASE MANAGEMENT',
        link: '/case-management',
      },
      {
        icon: '\u{1F9E9}',
        title: 'Workflow',
        desc: 'Document automation \u2014 contract review with redlining, risk reports, court forms, Counsel, and the family, criminal and property suites.',
        tag: 'WORKFLOW',
        link: '/workflow-features',
      },
      {
        icon: '\u26A1',
        title: 'Legal Research',
        desc: 'Comprehensive walkthrough of JurisTab\u2019s case law research suite \u2014 Ask JurisBot, Reasoning, Appearances, Brief, JurisLink, and filtered search.',
        tag: 'RESEARCH',
        link: '/features',
      },
      {
        icon: '\u{1F5C2}\uFE0F',
        title: 'Practice Areas',
        desc: 'Dedicated workflow suites for family, criminal and property practice, plus research support for corporate, employment, immigration and 13 more legal domains.',
        tag: 'PRACTICE AREAS',
        link: '/practice-areas',
      },
      {
        icon: '\u{1F4B3}',
        title: 'Pricing Plans',
        desc: `Pay as you go, or take one module \u2014 case management, workflow or legal research \u2014 from ${money(59)} a month. Every job quotes its cost before it runs.`,
        tag: 'PRICING',
        link: '/pricing',
      },
      {
        icon: '\u{1F4D6}',
        title: 'Blog & Insights',
        desc: '12 articles covering AI in law, legal research methodology, product updates, and the future of Australian legaltech.',
        tag: 'BLOG \u00B7 12 ARTICLES',
        link: '/blogs',
      },
      {
        icon: '\u{1F4AC}',
        title: 'Contact & Support',
        desc: `Reach out for support or inquiries. Email us at ${CONTACT_INFO.email} or call ${CONTACT_INFO.phone}.`,
        tag: 'SUPPORT',
        link: '/contact',
      },
    ],
  },

  blogs: {
    tag: 'Insights',
    title: 'From the ',
    titleAccent: 'JurisTab',
    titleSuffix: ' journal',
    viewAllText: 'View all 12 articles \u2192',
    viewAllLink: '/blogs',
    readMore: 'Read article \u2192',
    items: [
      {
        emoji: '\u{1F916}',
        thumbClass: 'blogThumb1' as const,
        category: 'AI & Legal Tech',
        date: '2025',
        title: 'How AI is Transforming Legal Research in 2025',
        desc: 'Discover how artificial intelligence is revolutionizing the way legal professionals conduct research, saving time and improving accuracy.',
        featured: true,
        slug: 'ai-transforming-legal-research',
      },
      {
        emoji: '\u{1F4DC}',
        thumbClass: 'blogThumb2' as const,
        category: 'AI & Legal Tech',
        date: '2025',
        title: 'Understanding Natural Language Processing in Law',
        desc: 'A deep dive into how NLP technology enables lawyers to search and analyze legal documents using everyday language.',
        featured: false,
        slug: 'natural-language-processing-in-law',
      },
      {
        emoji: '\u2696\uFE0F',
        thumbClass: 'blogThumb3' as const,
        category: 'AI & Legal Tech',
        date: '2025',
        title: 'AI Governance: Ethics in Legal Technology',
        desc: 'Understanding the ethical considerations and governance frameworks needed for responsible AI in the legal sector.',
        featured: false,
        slug: 'ai-governance-ethics-legal-tech',
      },
    ],
  },

  cta: {
    title: 'Run the matter, automate\nthe documents, ',
    titleAccent: 'research',
    titleSuffix: ' the law',
    subtitle:
      'Join legal professionals across Australia running case management, document workflow and case-law research in one place. Start free with 1,000 trial credits \u2014 no credit card required.',
    ctaPrimary: 'Create Free Account',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── About page ── */

export const ABOUT = {
  hero: {
    label: 'About Us',
    title: 'About JurisTab',
    subtitle: 'Law made readable and clear.',
  },

  description: {
    label: 'Who We Are',
    title: 'Redefining Legal Technology',
    text: 'An Australian legal-tech platform serving lawyers, researchers, and students. JurisTab brings case management, AI document workflow and legal research into one workspace \u2014 from opening the matter and filling the court form to finding the case that decides it.',
  },

  mission: {
    title: 'Our Mission',
    text: 'To break down barriers of cost and complexity in legal technology, making essential legal information accessible and understandable for everyone.',
  },

  vision: {
    title: 'Our Vision',
    text: 'To transform legal practice from an art based on precedent into a science driven by data, creating a more predictable, efficient, and equitable justice system for all.',
  },

  targetUsers: {
    label: 'Built For',
    title: 'Who We Serve',
    subtitle: 'Empowering every stakeholder in the legal ecosystem with intelligent tools.',
    items: [
      {
        title: 'Legal Professionals',
        description:
          'Practising lawyers and legal advisors who need rapid access to case law, legislation, and regulatory insights to serve their clients effectively.',
      },
      {
        title: 'Researchers',
        description:
          'Academic and policy researchers exploring legal trends, jurisdictional comparisons, and legislative history with AI-powered precision.',
      },
      {
        title: 'Students',
        description:
          'Law students and trainees building foundational knowledge through intuitive, technology-enhanced legal research tools.',
      },
    ],
  },

  products: {
    label: 'Our Products',
    title: 'Powerful Tools for Legal Minds',
    subtitle: 'Two purpose-built products designed to transform how you practise and interact with the law.',
    items: [
      {
        title: 'JurisTab',
        description:
          'Our flagship platform: case management for the whole matter, 44 AI workflow services \u2014 including dedicated family, criminal and property suites \u2014 Counsel for questions across a matter, and research across 600,000+ Australian cases and 100,000+ legislation records.',
        link: APP_URL,
      },
      {
        title: 'DocuDive',
        description:
          'Intelligent document analysis that transforms dense legal documents into structured, actionable summaries. Upload any legal document and let AI do the heavy lifting.',
        link: DOCUDIVE_URL,
      },
    ],
  },

  cta: {
    title: 'Ready to Transform Your Legal Research?',
    subtitle: 'Join thousands of legal professionals who trust JurisTab for faster, smarter research.',
    ctaPrimary: 'Get Started Free',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── Pricing page ── */

export const PRICING = {
  hero: {
    label: 'Pricing',
    title: 'Simple Pricing, Real Numbers',
    subtitle:
      'Start with 1,000 free trial credits. Then pay as you go, or subscribe to the module you actually live in — case management, legal research or document workflow. All prices in Australian dollars, and you always see the exact cost before a job runs.',
  },

  featuredBadge: 'Best value',

  plans: [
    {
      name: 'Pay As You Go',
      description: 'No monthly fee. Buy credits, spend them only when you run something.',
      price: '50',
      currency: CURRENCY_SYMBOL,
      period: '/ 500 credits',
      features: [
        `10 credits = ${money(1)}. Packs: ${money(50)} = 500 credits, ${money(150)} = 1,650, ${money(400)} = 4,800`,
        `Workflow: 1–3 credits per page (${money(0.1, 2)}–${money(0.3, 2)}) depending on the service`,
        'Research: keyword search free; case unlock 20 credits, legislation 10, JurisBot 7',
        'Case management: 15 credits per contact record — matters and time are free',
        'Credits stay valid for 365 days — and pause if you subscribe later',
        `Example: a 60-page contract review = 180 credits = ${money(18)}`,
      ],
      cta: 'Buy Credits',
      ctaLink: APP_URL,
    },
    {
      name: 'Case Management',
      description: 'Run the practice: intake, matters, calendar, time, billing and signing.',
      price: '59',
      currency: CURRENCY_SYMBOL,
      period: '/ user / month',
      features: [
        'Unlimited matters, contacts, tasks and legal calendaring',
        'Time and expense recording, invoices and issued tax invoices',
        'Documents, e-signatures and reports across every matter',
        `${money(590)} a year — two months free`,
        'Workflow and legal research still available, charged from credits',
        `Example: 40 new contacts a month costs ${money(60)} on credits — ${money(59)} here, unlimited`,
      ],
      cta: 'Start Case Management',
      ctaLink: APP_URL,
    },
    {
      name: 'Legal Research',
      description: 'Unlimited research for lawyers who live in case law and legislation.',
      price: '159',
      currency: CURRENCY_SYMBOL,
      period: '/ user / month',
      features: [
        'Unlimited case unlocks, legislation point-in-time and JurisBot',
        'Reasoning, Appearances, Brief and JurisLink on every case you open',
        `${money(1590)} a year — two months free`,
        'Workflow automation still available, charged from credits at the usual rates',
        'Up to 250 pages per document, 20 documents per job, 5 jobs at once',
        `Example: 100 case unlocks would be ${money(200)} on credits — ${money(159)} here`,
      ],
      cta: 'Start Legal Research',
      ctaLink: APP_URL,
    },
    {
      name: 'Workflow',
      description: 'For document-heavy practices: review, redline, extract, draft.',
      price: '169',
      currency: CURRENCY_SYMBOL,
      period: '/ user / month',
      features: [
        '1,200 processed pages included every month',
        'Every service costs one allowance page per page — light or heavy',
        `Extra pages ${money(0.14, 2)} each, settled on your next invoice`,
        `${money(1690)} a year — two months free`,
        'Up to 100 pages per document, 10 documents per job, 2 jobs at once',
        'One brand template, so generated documents come out on your letterhead',
      ],
      cta: 'Start Workflow',
      ctaLink: APP_URL,
    },
    {
      name: 'Pro+',
      description: 'All three modules, unlimited research, and our largest page allowance.',
      price: '299',
      currency: CURRENCY_SYMBOL,
      period: '/ user / month',
      features: [
        'Less than Legal Research and Workflow bought separately',
        'Unlimited case management and unlimited legal research',
        '3,000 processed pages a month, plus 750 pages of rollover',
        `Extra pages ${money(0.1, 2)} each, settled on your next invoice`,
        `${money(2990)} a year — two months free`,
        'Up to 250 pages per document, 20 documents per job, 5 jobs at once',
        'Five brand templates for generated documents',
      ],
      cta: 'Get Pro+',
      ctaLink: APP_URL,
      featured: true,
    },
  ],

  guide: {
    label: 'What things cost',
    title: 'The Rate Card, in Plain Numbers',
    subtitle:
      `Credits are the single unit of usage: 10 credits = ${money(1)}. Workflow is charged per processed page, research and case management per action, and nothing runs before you have seen the price.`,
    table: {
      title: 'Rate Card',
      headers: ['What you run', 'Rate', 'In dollars', 'Worked example'],
      rows: [
        {
          from: 'Upload, OCR, matter organisation, export to Word or PDF',
          to: 'Free',
          charged: `${money(0)} — always`,
          how: 'Reading and organising your own files is never metered, on any plan.',
        },
        {
          from: 'Keyword search',
          to: 'Free',
          charged: `${money(0)} — always`,
          how: 'Search is unlimited on every plan, including pay as you go.',
        },
        {
          from: 'Matters, tasks, calendar entries, time and expense records',
          to: 'Free',
          charged: `${money(0)} — always`,
          how: 'Running the matter file costs nothing. Only a new contact record is metered.',
        },
        {
          from: 'Contact record — case management',
          to: '15 credits',
          charged: `${money(1.5, 2)} a contact`,
          how: 'Charged once, when the contact is created. Unlimited on Case Management and Pro+.',
        },
        {
          from: 'Case unlock',
          to: '20 credits',
          charged: `${money(2, 2)} a case`,
          how: 'Unlocks Reasoning, Appearances, Brief and JurisLink. Reopening the same case is free for 30 days.',
        },
        {
          from: 'Legislation section unlock',
          to: '10 credits',
          charged: `${money(1, 2)} a section`,
          how: 'Point-in-time text, free to reopen for 30 days. Unlimited on Legal Research and Pro+.',
        },
        {
          from: 'JurisBot message',
          to: '7 credits',
          charged: `${money(0.7, 2)} a message`,
          how: `10 questions = 70 credits = ${money(7)}. Unlimited on Legal Research and Pro+.`,
        },
        {
          from: 'Light workflow — Document Organiser, Annotation & Highlights, Party & Defined Terms, Plain-Language Explainer',
          to: '1 credit a page',
          charged: `${money(0.1, 2)} a page`,
          how: `A 20-page bundle = 20 credits = ${money(2)}.`,
        },
        {
          from: 'Standard workflow — Risk & Issues Report, Obligations & Encumbrance Memo, Document Matrix Review, Document Summary, Clause Extraction, Obligation Checklist, Key Dates',
          to: '2 credits a page',
          charged: `${money(0.2, 2)} a page`,
          how: `A 40-page report = 80 credits = ${money(8)}.`,
        },
        {
          from: 'Heavy workflow — Contract & Disclosure Review, Matter Research, Clause Comparison & Redline, Draft Generation, Special Conditions, Vendor Statement',
          to: '3 credits a page',
          charged: `${money(0.3, 2)} a page`,
          how: `A 60-page contract = 180 credits = ${money(18)}.`,
        },
      ],
    },
    sections: [
      {
        title: 'How credits work',
        points: [
          `10 credits = ${money(1)}. Every price on this page converts at that rate.`,
          `Buy in packs: ${money(50)} = 500 credits, ${money(150)} = 1,650 credits, ${money(400)} = 4,800 credits.`,
          `Larger packs carry a bonus — the ${money(400)} pack works out at 12 credits per dollar instead of 10.`,
          'Credits are valid for 365 days from purchase.',
          'Subscribe later and that countdown pauses; if the plan ends, it restarts from the same number of days.',
          'Turn on auto top-up and we buy your usual pack whenever your balance drops below 100 credits.',
        ],
      },
      {
        title: 'A month in the life',
        points: [
          'A property lawyer opens 5 new client contacts, runs one 60-page contract review and one 40-page risk report, and unlocks 5 cases.',
          `On pay as you go that is 75 + 180 + 80 + 100 credits = 435 credits, or ${money(43.5, 2)} for the month.`,
          'At that volume credits are still cheaper than any subscription — so stay on pay as you go.',
          `Case Management at ${money(59)} overtakes credits at about 40 new contacts a month.`,
          `Legal Research at ${money(159)} overtakes credits at about 80 case unlocks a month.`,
          `Workflow at ${money(169)} overtakes credits past roughly 560 pages of heavy review a month.`,
          'Your dashboard does this comparison for you each month and tells you which is cheaper.',
        ],
      },
      {
        title: 'Allowances and overage',
        points: [
          'On Workflow and Pro+ every processed page costs one allowance page — light or heavy, it makes no difference.',
          'The full month of pages lands the day you subscribe, whatever the date.',
          'Go past the allowance and we use your credits next; only when those run out do you pay overage.',
          `Overage is the plan’s own page rate — ${money(0.14, 2)} a page on Workflow, ${money(0.1, 2)} on Pro+ — added to your next invoice.`,
          'Set a monthly overage cap and we pause new jobs at the cap rather than quietly billing more.',
          'Pro+ rolls up to 25% of an unused allowance — 750 pages — into the next month only. It is not cumulative.',
        ],
      },
      {
        title: 'Upgrading, downgrading, cancelling',
        points: [
          'Upgrade any time: the new features and the full new allowance unlock immediately, and your renewal date does not move.',
          `You pay only the difference, prorated for the days left — moving from Workflow to Pro+ with 10 of 30 days remaining costs about ${money(43)}.`,
          'A downgrade costs nothing now and takes effect at your next renewal; the plan you paid for runs to the end.',
          'One plan change per billing period, so the change you make is the one that sticks.',
          'Cancel and you keep everything you paid for until the period ends, then move to pay as you go.',
          'A failed payment never cancels anything — your current plan stays active while you retry or update your card.',
        ],
      },
      {
        title: 'When a plan ends',
        points: [
          'Unused allowance and rollover pages are forfeited — they belong to the period they were granted for.',
          'Purchased credits are untouched, and their 365-day clock restarts from exactly where it paused.',
          'Matters, documents, outputs, invoices and history stay readable. Nothing is deleted.',
          'You are on pay as you go from that moment, and can start new work as soon as you have credits.',
        ],
      },
      {
        title: 'Trials, quotes and refunds',
        points: [
          'New accounts get 1,000 trial credits, valid for 7 days. No card required.',
          'That is enough to take two matters end to end before you decide anything.',
          'Every job shows its exact cost before it runs, and you are never charged more than that quote.',
          'If a job fails on our side, the credits go straight back to the bucket they came from.',
          'Unused credits are refundable within 7 days of purchase, provided less than 10% of the pack has been used.',
          'Subscription fees are never paid with credits, and credits are never spent on a subscription — the two meters stay separate.',
        ],
      },
    ],
  },

  faq: {
    label: 'FAQ',
    title: 'Frequently Asked Questions',
    subtitle: 'Straight answers about credits, allowances and plans',
    items: [
      {
        question: 'Which plan should I be on?',
        answer:
          `Start on pay as you go — there is no monthly fee, so you only pay for what you run. Move to Case Management (${money(59)}) once you are opening more than about 40 new contacts a month. Move to Legal Research (${money(159)}) once you unlock more than about 80 cases a month. Move to Workflow (${money(169)}) once you process more than about 560 pages of heavy review a month. Take Pro+ (${money(299)}) if you need more than one of them — it costs less than Legal Research and Workflow bought separately. Your dashboard compares last month’s usage against every plan and tells you which would have been cheapest.`,
      },
      {
        question: 'What is a credit worth?',
        answer:
          `10 credits = ${money(1)}, so one credit is 10 cents. Keyword search is free on every plan. A case unlock is 20 credits (${money(2, 2)}), a legislation section is 10 (${money(1, 2)}), a JurisBot message is 7 (${money(0.7, 2)}), a new contact record is 15 (${money(1.5, 2)}), and workflow pages are 1 to 3 credits each (${money(0.1, 2)}–${money(0.3, 2)}) depending on how heavy the service is. Packs start at ${money(50)} for 500 credits, and larger packs include bonus credits — ${money(400)} buys 4,800 credits, which is 12 credits per dollar.`,
      },
      {
        question: 'What does case management cost to run?',
        answer:
          `Almost nothing on pay as you go. Matters, tasks, the calendar, time and expense records, documents, invoices and e-signature envelopes are not metered at all — only creating a new contact record is, at 15 credits (${money(1.5, 2)}). So a practice that opens ten new clients a month spends ${money(15)} on credits. Past roughly 40 new contacts a month the ${money(59)} Case Management plan, which makes all of it unlimited, is the cheaper answer. Pro+ includes case management as well.`,
      },
      {
        question: 'What counts as a processed page?',
        answer:
          'A page that a workflow service actually reads and works on. Uploading, OCR, organising a matter and exporting are never counted. On the credit rate card a page costs 1, 2 or 3 credits depending on the service. On a subscription it is simpler: every processed page uses exactly one page of your monthly allowance, whether it came from the lightest or the heaviest service.',
      },
      {
        question: 'Do credits expire?',
        answer:
          'Purchased credits are valid for 365 days. If you subscribe, the countdown pauses for as long as your plan runs — you are using the plan’s allowance, not your credits, so it would be unfair to burn the clock. If the plan later ends, the countdown restarts from exactly the number of days it had left. Trial credits are different: they last 7 days from activation and do not pause.',
      },
      {
        question: 'What happens if I go over my monthly allowance?',
        answer:
          `Nothing stops. We use your allowance first, then any credits you hold, and only after both are exhausted do you pay overage — ${money(0.14, 2)} a page on Workflow, ${money(0.1, 2)} a page on Pro+ — which is added to your next invoice. You can set a monthly overage cap; when you reach it we pause new jobs and tell you, instead of continuing to bill. Pro+ also rolls up to 750 unused pages into the following month.`,
      },
      {
        question: 'Is there a free trial?',
        answer:
          'Yes. New accounts receive 1,000 trial credits valid for 7 days, with no card required. That is sized to take two matters end to end — open the clients, run a bundle through three services, unlock a couple of cases and ask JurisBot a few questions — so you can judge the output on your own files. When the trial ends you simply move to pay as you go: you keep read access to everything you produced, and can start new work again as soon as you buy credits or subscribe.',
      },
      {
        question: 'Can I switch plans at any time?',
        answer:
          'Yes. Upgrades take effect immediately: the full new allowance is granted straight away, your renewal date stays where it is, and you pay only the price difference prorated across the days remaining. Downgrades cost nothing now and take effect at your next renewal, so you keep the plan you have already paid for until the end of its period. You can make one plan change per billing period.',
      },
      {
        question: 'What if a job fails, or I bought credits I do not need?',
        answer:
          'If a job fails on our side it is refunded automatically, back to the same credits or allowance it was taken from. If it failed because of the file or input you supplied, it is refunded automatically for 30 days and by request after that. Unused credit packs are refundable within 7 days of purchase as long as you have used less than 10% of them, refunded pro rata on the unspent portion.',
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'All major credit and debit cards — Visa, Mastercard and American Express — processed securely through Stripe. Cards can be saved for renewals and auto top-ups. Teams, universities and enterprise accounts can also be invoiced; talk to us and we will set it up.',
      },
      {
        question: 'Do you offer student or academic pricing?',
        answer:
          'Yes. Students and academic institutions can be granted access with the same entitlements as Pro+ — case management, unlimited research and 3,000 pages a month — over a date range we agree with you, rather than a card subscription. Get in touch with your institution details and we will arrange it.',
      },
    ],
  },

  cta: {
    title: 'Teams, Universities and Enterprise',
    subtitle:
      'Seat-based billing, volume page rates, student and academic access, invoicing and onboarding. Tell us roughly how many matters, pages and cases your team runs each month and we will price it for you.',
    button: 'Talk To Us',
    buttonLink: '/contact',
  },
};

/* ── Case Management page ── */

export const CASE_MANAGEMENT_PAGE = {
  hero: {
    label: 'Case Management',
    title: 'The Whole Matter, ',
    titleAccent: 'in One File',
    subtitle:
      'Client intake and conflict checks, matters and parties, documents, time and expenses, invoicing, calendaring, online meetings, court forms and e-signatures \u2014 the practice management your firm runs on, sharing the same matters as the AI workflow and research it sits beside.',
  },

  section: {
    label: 'What We Manage',
    title: 'Twelve Modules. One Matter File.',
    subtitle: 'Click a module to see how it handles its part of running the practice.',
  },

  contentNotAvailable: 'Content not available.',

  cta: {
    title: 'Ready to run your matters in one place?',
    subtitle:
      `Case management from ${money(59)} a month, or free on pay as you go until you are opening more than about 40 new contacts a month \u2014 matters, time and invoicing are never metered.`,
    button: 'Sign Up',
    buttonLink: APP_URL,
  },
};

/* ── Features page (Legal Research) ── */

export const FEATURES_PAGE = {
  hero: {
    label: 'Legal Research',
    title: 'Legal Information ',
    titleAccent: 'Simplified',
    subtitle:
      'Empowering legal professionals through AI-driven case law research, judicial reasoning visualisation, and structured insights from Australian judgments.',
  },

  section: {
    label: 'What We Offer',
    title: 'Powerful Tools for Legal Professionals',
    subtitle: 'Click a feature to explore how it works and what it can do for you.',
  },

  contentNotAvailable: 'Content not available.',

  cta: {
    title: 'Ready to transform your legal research?',
    subtitle:
      'Join thousands of legal professionals who trust JurisTab for smarter, faster, and more insightful legal work.',
    button: 'Sign Up',
    buttonLink: APP_URL,
  },
};

/* ── Workflow page ── */

export const WORKFLOW_FEATURES_PAGE = {
  hero: {
    label: 'Workflow',
    title: 'Document Work, ',
    titleAccent: 'Automated',
    subtitle:
      'Agentic AI that reads, reviews, drafts, and organises Australian legal documents end-to-end. From redlined contract reviews to completed court forms, with dedicated suites for family, criminal and property practice — built for the way legal teams actually work.',
  },

  section: {
    label: 'What We Automate',
    title: '44 Services. One Workflow Engine.',
    subtitle: 'Click a service or practice-area suite to see how it streamlines a specific stage of legal document work.',
  },

  contentNotAvailable: 'Content not available.',

  cta: {
    title: 'Ready to automate your document workflow?',
    subtitle:
      'Combine these services into pipelines that handle review, drafting, and organisation — so your team can focus on judgment, not busywork.',
    button: 'Sign Up',
    buttonLink: APP_URL,
  },
};

/* ── Blogs page ── */

export const BLOGS_PAGE = {
  hero: {
    label: 'Knowledge Hub',
    title: 'Blog & Insights',
    subtitle: 'Explore the latest in legal technology, AI research, and industry trends.',
  },
  categories: ['All', 'AI & Legal Tech', 'Research Tips', 'Industry News'],
  loading: 'Loading blogs...',
  noPosts: 'No posts found.',
  imgAlt: 'Blog Image',
};

/* ── Blog post page ── */

export const BLOG_POST_PAGE = {
  loading: 'Loading...',
  notFound: 'Post not found',
  backText: 'Back to Blog',
  backLink: '/blogs',
  shareLabel: 'Share',
  relatedTitle: 'Related Posts',
  shareTitle: 'Share This Post',
};

/* ── Contact page ── */

export const CONTACT = {
  hero: {
    label: 'Contact',
    title: 'Get in Touch',
    subtitle: 'Have a question or want to learn more? We would love to hear from you.',
  },

  form: {
    sectionTitle: "Let's Start a Conversation",
    sectionDesc:
      'Whether you have a question about our products, need technical support, or want to explore partnership opportunities, our team is here to help.',
    socialLabel: 'Follow Us',
    fields: {
      firstName: { label: 'First Name', placeholder: 'John' },
      lastName: { label: 'Last Name', placeholder: 'Doe' },
      email: { label: 'Email', placeholder: 'john@example.com' },
      phone: { label: 'Phone', placeholder: '+61 400 000 000' },
      subject: {
        label: 'Subject',
        placeholder: 'Select a subject',
        options: [
          'General Inquiry',
          'Technical Support',
          'Sales & Pricing',
          'Partnerships',
          'Feedback',
          'Other',
        ],
      },
      message: { label: 'Message', placeholder: 'Tell us how we can help...' },
    },
    submitButton: 'Send Message',
    sendingButton: 'Sending…',
  },

  map: REGION.office && {
    label: REGION.office.label,
    query: REGION.office.label,
    title: REGION.office.mapTitle,
    latitude: REGION.office.latitude,
    longitude: REGION.office.longitude,
    directionsUrl: REGION.office.directionsUrl,
  },
};

/* ── Responsible AI: 5P governance tab ── */

export const RESPONSIBLE_AI_GOVERNANCE = {
  key: 'ai-governance',
  label: 'AI Governance',
  title: 'JurisTab\'s 5P Governance Framework',
  subtitle: 'Policy, Protection, Platform, Proof and Persistence: five pillars governing every AI system across its lifecycle.',
  image: '/images/responsible-ai/ai-governance-5p.jpg',
  vision: 'To reap AI benefits ethically, safely, and responsibly.',
  intro:
    'The 5P Framework establishes a comprehensive governance structure that enables JurisTab to harness the transformative power of AI while maintaining the highest standards of ethics, security, privacy, and operational excellence aligned with Australian regulations and international best practices.',
  frameworkIntro:
    'This framework ensures our AI systems—including natural language search, Appearances, Brief, JurisLink, and DocuDive—operate transparently, accurately, and in compliance with Australian regulations and international best practices.',
  frameworkDefinition: [
    { label: 'WHY', description: 'To ensure all AI initiatives deliver value while protecting customers, employees, and the organisation from AI-related risks.' },
    { label: 'WHAT', description: 'A governance framework covering 5 pillars: Policy, Protection, Platform, Proof, and Persistence.' },
    { label: 'WHEN', description: 'Applied throughout the AI lifecycle: from project initiation, through development and deployment, to ongoing operations.' },
    { label: 'HOW', description: 'Through the AI Council governance structure, standardised risk assessments, the 5P Checklist, and continuous monitoring.' },
    { label: 'WHO', description: 'The AI Council provides oversight. Technology leadership and the AI Engineering Lead drive implementation.' },
    { label: 'WHERE', description: 'All JurisTab products and services. Covers cloud-hosted, on-premise, and hybrid AI solutions.' },
  ],
  /* Descriptions support inline [label](url) links */
  items: [
    {
      title: 'Policy',
      category: 'Legal, Privacy & Compliance',
      description:
        'Adherence to Australian AI policy and privacy law, privacy by design, responsible data governance, and the regulations that apply to legal practice.',
      subItems: [
        { title: 'Australian AI Policy', description: 'Adherence to [Australia’s AI Ethics Principles](https://www.industry.gov.au/publications/australias-artificial-intelligence-ethics-principles/australias-ai-ethics-principles), [Voluntary AI Safety Standard (10 Guardrails)](https://www.industry.gov.au/publications/voluntary-ai-safety-standard/10-guardrails), and [Guidance for AI Adoption](https://www.industry.gov.au/publications/guidance-for-ai-adoption). [See an example](https://webarchive.nla.gov.au/awa/20250624144741/https://www.industry.gov.au/news/australian-ai-ethics-principles-commonwealth-bank-tests-bill-sense).' },
        { title: 'Privacy by Design', description: 'Privacy Act 1988 compliance, Australian Privacy Principles (APPs), [OAIC guidelines for GenAI](https://www.oaic.gov.au/privacy/privacy-guidance-for-organisations-and-government-agencies/guidance-on-privacy-and-developing-and-training-generative-ai-models).' },
        { title: 'Data Governance', description: 'PII data handling across storage, processing, transmission, and retention; data minimisation; consent management.' },
        { title: 'Legal Investigation', description: 'Data handling and retention policies supporting security and legal investigation requirements.' },
        { title: 'Industry Regulations', description: '[Legal-sector regulations, consumer protection](https://humanrights.gov.au/our-work/submissions/technology-and-human-rights/guardrails-high-risk-ai), and anti-discrimination compliance.' },
      ],
    },
    {
      title: 'Protection',
      category: 'Security & Guardrails',
      description:
        'Security by design, defence in depth, AI-specific guardrails, bounded agentic AI, and integrated security operations.',
      subItems: [
        { title: 'Cyber Security by Design', description: 'Security integrated from inception all the way through to design, build, test and release; threat modelling, vulnerability assessments, tooling, penetration testing.' },
        { title: 'Defence in Depth', description: 'Multiple security layers, network segmentation, access controls, IP filtering and firewall protection, encryption at rest and in transit.' },
        { title: 'AI-Specific Guardrails', description: 'Prompt injection prevention, output filtering, [content safety](https://www.industry.gov.au/publications/voluntary-ai-safety-standard/10-guardrails), model access controls, ethical and responsible AI controls.' },
        { title: 'Agentic AI Framework', description: '[Agent boundaries](https://www.industry.gov.au/publications/voluntary-ai-safety-standard/10-guardrails), permission models, human-in-the-loop controls, autonomous action limits.' },
        { title: 'SecOps Integration', description: 'Security operations centre integration, incident response, threat monitoring, SOC alerting.' },
      ],
    },
    {
      title: 'Platform',
      category: 'Architecture & Build Standards',
      description:
        'Enterprise architecture alignment, multi-agent communication, integration and infrastructure standards, and disciplined model management.',
      subItems: [
        { title: 'Enterprise Architecture', description: '[EA capability assessment](https://www.industry.gov.au/publications/guidance-for-ai-adoption), alignment with enterprise standards, and technology roadmap integration.' },
        { title: 'Agent-to-Agent (A2A)', description: 'Communication standards for multi-agent systems, protocol definitions, message formats.' },
        { title: 'Integration Standards', description: 'API standards, data exchange formats, service mesh requirements, event-driven architecture.' },
        { title: 'Model Management', description: 'Model versioning, model registry, deployment pipelines, rollback capabilities.' },
        { title: 'Infrastructure Standards', description: 'Cloud architecture patterns, compute requirements, scaling policies, cost management.' },
      ],
    },
    {
      title: 'Proof',
      category: 'Test, Deploy, Evaluate & Monitor',
      description:
        'Evidence before trust: testing agents and models against business outcomes, evaluation and bias benchmarks, performance testing, observability, and quality gates.',
      subItems: [
        { title: 'Testing Approach', description: 'Testing for agents, LLM models, and integrations, measuring accuracy and output against expected business outcomes.' },
        { title: 'Evaluation Framework', description: 'Model evaluation standards, benchmark definitions, performance metrics, bias detection.' },
        { title: 'Performance Testing (SVT)', description: 'Stress and volume testing, load testing, latency benchmarks, throughput requirements.' },
        { title: 'Observability & Monitoring', description: 'Monitoring standards, tooling & framework selection, alerting thresholds, dashboards.' },
        { title: 'Quality Gates', description: 'Acceptance criteria, go/no-go decision frameworks, release certification.' },
      ],
    },
    {
      title: 'Persistence',
      category: 'Operate, Resilience, Audit & Optimise',
      description:
        'Keeping AI dependable over time: high availability and recovery, integrated service management, full traceability, retention, and continuous improvement.',
      subItems: [
        { title: 'Operational Excellence', description: 'High Availability (HA), Disaster Recovery (DR), defined RTO and RPO targets.' },
        { title: 'Enterprise Integration', description: 'Incident Management, Problem Management, and Change Management processes.' },
        { title: 'Logging & Auditing', description: '[OpenTelemetry](https://opentelemetry.io/) implementation, full traceability of customer/agent transactions across all solution components.' },
        { title: 'Retention Policy', description: 'Log retention policy definition and implementation, compliance with legal hold requirements.' },
        { title: 'Analytics for Improvement', description: 'Analysis of user types and prompts/requests for continuous improvement and optimisation.' },
      ],
    },
  ],
  council: {
    intro:
      'The AI Council provides cross-functional oversight and governance for all AI initiatives across JurisTab.',
    roles: [
      { role: 'AI Council', responsibility: 'Strategic oversight, policy approval, risk acceptance for HIGH/CRITICAL projects, dispute resolution.', cadence: 'Monthly + ad-hoc for escalations' },
      { role: 'AI Engineering Lead', responsibility: 'Framework ownership, project portfolio oversight, vendor management.', cadence: 'Weekly reviews' },
      { role: 'Security Lead', responsibility: 'Protection assessment, security architecture review, SecOps integration, incident response.', cadence: 'Per project' },
      { role: 'Privacy/Legal Lead', responsibility: 'Policy assessment, privacy impact reviews, legal risk evaluation, policy alignment.', cadence: 'Per project' },
      { role: 'Enterprise Architect', responsibility: 'Architecture review, integration standards, platform assessment, technical debt management.', cadence: 'Per project' },
      { role: 'Project Managers', responsibility: 'Project delivery, 5P checklist completion, risk identification, stakeholder management.', cadence: 'Continuous' },
      { role: 'Vendors & Partners', responsibility: 'Comply with the 5P Framework, provide the required documentation, and participate in reviews.', cadence: 'Per project requirements' },
    ],
    keyPrinciple:
      'Leaders cannot delegate or outsource accountability for AI systems. While tasks may be delegated to vendors or team members, accountability for AI governance remains with designated JurisTab leaders as per [Australian AI Safety Standard Guardrail 1](https://www.industry.gov.au/publications/voluntary-ai-safety-standard/10-guardrails).',
  },
  workflow: [
    'Register project in the AI Project Register',
    'Complete the 5P Assessment Checklist',
    'Submit to the AI Engineering Lead for review',
    'Obtain required approvals based on risk level',
    'Deploy and maintain continuous monitoring',
  ],
  references: {
    intro: 'The 5P Framework is inspired by and aligned with the following Australian and international standards:',
    groups: [
      {
        title: 'Australian Government Frameworks',
        items: [
          { name: 'Australia’s AI Ethics Principles', description: '8 voluntary principles including Human rights, Wellbeing, Transparency, Contestability, Accountability, Fairness, Privacy, Reliability & Safety.', link: 'https://www.industry.gov.au/publications/australias-ai-ethics-principles' },
          { name: 'Voluntary AI Safety Standard', description: '10 Guardrails including accountability, risk management, data governance, human oversight, and incident response.', link: 'https://www.industry.gov.au/publications/voluntary-ai-safety-standard' },
          { name: 'Guidance for AI Adoption', description: '6 essential practices: Understand, Govern, Collaborate, Protect, Inform, Review.', link: 'https://www.industry.gov.au/publications/guidance-for-ai-adoption' },
          { name: 'OAIC Privacy Guidelines for AI', description: 'Privacy Act 1988 compliance, guidance on GenAI development, training, and commercial AI product usage.', link: 'https://www.oaic.gov.au/privacy/privacy-guidance-for-organisations-and-government-agencies' },
        ],
      },
      {
        title: 'International Standards',
        items: [
          { name: 'NIST AI Risk Management Framework', description: 'Four core functions: Govern, Map, Measure, Manage.', link: 'https://www.nist.gov/itl/ai-risk-management-framework' },
          { name: 'ISO/IEC 42001:2023', description: 'AI Management System standard for establishing, implementing, and improving AI management.', link: 'https://www.iso.org/standard/42001' },
          { name: 'EU AI Act', description: 'Risk-based regulatory framework classifying AI systems by risk level.', link: 'https://artificialintelligenceact.eu/' },
          { name: 'OECD AI Principles', description: 'International principles for trustworthy AI adopted by 46 countries including Australia.', link: 'https://oecd.ai/en/ai-principles' },
        ],
      },
      {
        title: 'Technical Standards',
        items: [
          { name: 'NIST Cybersecurity Framework', description: 'Identify, Protect, Detect, Respond, Recover functions.', link: 'https://www.nist.gov/cyberframework' },
          { name: 'OpenTelemetry', description: 'Observability framework for traces, metrics, and logs.', link: 'https://opentelemetry.io/' },
          { name: 'TOGAF', description: 'Enterprise architecture framework for business, data, application, and technology architecture.', link: 'https://www.opengroup.org/togaf' },
          { name: 'ITIL 4', description: 'IT service management framework for incident, problem, and change management.', link: 'https://www.axelos.com/certifications/itil-service-management' },
        ],
      },
    ],
  },
};

/* ── Responsible AI page ── */

export const RESPONSIBLE_AI = {
  hero: {
    label: 'Responsible AI',
    title: 'Responsible AI at JurisTab',
    subtitle:
      'Our commitment to ethical, transparent, and accountable AI in legal technology.',
  },

  tabs: [
    {
      key: 'ai-ethics',
      label: 'AI Ethics',
      title: 'Ethical AI in Legal Technology',
      subtitle: 'How JurisTab upholds ethical standards across every AI-powered feature.',
      image: '/images/responsible-ai/ai-ethics.png',
      intro:
        'At JurisTab, ethical AI is not an afterthought — it is embedded into the design, development, and deployment of every feature. We believe that AI in the legal domain carries heightened responsibility because the outcomes directly impact justice, rights, and livelihoods.',
      items: [
        {
          title: 'Fairness & Non-Discrimination',
          description:
            'Our AI models are regularly audited for bias across jurisdictions, case types, and demographics. We ensure that search results and summaries do not favour or disadvantage any party unfairly.',
        },
        {
          title: 'Transparency & Explainability',
          description:
            'Every AI-generated output — from case summaries to citation links — includes provenance indicators so users can trace how a result was derived and verify it against primary sources.',
        },
        {
          title: 'User Privacy & Consent',
          description:
            'No training occurs on user queries without explicit consent. Your research stays yours. We adhere to privacy-by-design principles aligned with the Privacy Act 1988.',
        },
        {
          title: 'Human-Centred Design',
          description:
            'AI assists rather than replaces professional legal judgment. Human oversight remains central to every workflow, ensuring practitioners retain full control over their work product.',
        },
      ],
    },
    RESPONSIBLE_AI_GOVERNANCE,
    {
      key: 'ai-principles',
      label: 'AI Principles',
      title: 'Our Core AI Principles',
      subtitle: 'The foundational principles that guide every AI decision at JurisTab.',
      image: '/images/responsible-ai/ai-principles.png',
      intro:
        'Our AI principles are informed by Australia\'s AI Ethics Principles and shaped by the unique demands of the legal profession. They serve as the north star for product development, model selection, and platform operations.',
      items: [
        {
          title: 'Accuracy Above All',
          description:
            'Legal AI must be accurate. We implement citation accuracy checks, confidence scoring thresholds, and multi-source verification to prevent AI misinformation in legal research outputs.',
        },
        {
          title: 'Accountability & Auditability',
          description:
            'Every AI decision is logged and traceable. We maintain comprehensive audit trails so that outputs can be reviewed, challenged, and explained at any point.',
        },
        {
          title: 'Proportionality',
          description:
            'AI is deployed only where it adds genuine value without introducing disproportionate risk. We carefully assess each use case to ensure AI augments — never undermines — the integrity of legal work.',
        },
        {
          title: 'Continuous Improvement',
          description:
            'Our models and safeguards evolve with the legal landscape. We regularly review performance benchmarks, user feedback, and regulatory changes to refine our AI systems.',
        },
      ],
    },
    {
      key: 'strategic-imperatives',
      label: 'Strategic Imperatives',
      title: 'Strategic AI Imperatives',
      subtitle: 'The strategic commitments driving JurisTab\'s responsible AI roadmap.',
      image: '/images/responsible-ai/strategic-imperatives.png',
      intro:
        'Beyond principles and governance, JurisTab is committed to a set of strategic imperatives that shape our long-term AI roadmap. These imperatives ensure that our technology serves the evolving needs of Australian legal professionals while maintaining the highest standards of responsibility.',
      items: [
        {
          title: 'Sovereign Data Practices',
          description:
            'All legal data is processed and stored within Australian jurisdiction. We are committed to data sovereignty, ensuring that sensitive legal information never leaves the regulatory framework it belongs to.',
        },
        {
          title: 'Open Standards & Interoperability',
          description:
            'We build on open standards wherever possible, ensuring that JurisTab integrates seamlessly with existing legal workflows, practice management systems, and regulatory reporting requirements.',
        },
        {
          title: 'Industry Collaboration',
          description:
            'We actively engage with legal professional bodies, regulators, and academic institutions to co-develop AI standards that reflect the realities of legal practice in Australia.',
        },
        {
          title: 'Resilience & Business Continuity',
          description:
            'Our platform is designed for high availability with incident management procedures, retention policies aligned with legal requirements, and failover mechanisms that ensure uninterrupted access to critical legal research tools.',
        },
      ],
    },
  ],

  cta: {
    title: 'Trust Starts with Transparency',
    subtitle:
      'We believe responsible AI is not optional — it is foundational. Explore how JurisTab puts ethical AI into practice.',
    ctaPrimary: 'Get Started Free',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── Data Privacy & Security page ── */

export const DATA_PRIVACY = {
  hero: {
    label: 'Data Privacy & Security',
    title: 'Your Data, Protected by Design',
    subtitle:
      'How JurisTab secures client data on AWS — encryption, access controls, sovereignty, and the safeguards behind every workflow.',
  },

  summary: {
    intro:
      'JurisTab is built on Amazon Web Services (AWS), the same infrastructure trusted by global banks, government agencies, and major law firms. Every layer of our platform — from storage and transmission to AI processing and access — is engineered around the principle that legal data is privileged data. This page summarises the technical and organisational measures we take to keep it that way.',
    pillars: [
      { label: 'Hosted on AWS', value: 'Tier-1 cloud infrastructure with ISO 27001, SOC 1/2/3, and PCI-DSS certifications inherited from AWS.' },
      { label: 'Encrypted End-to-End', value: 'AES-256 encryption at rest; TLS 1.3 in transit. Keys managed through AWS KMS.' },
      { label: 'Data Sovereignty', value: 'Customer data is processed and stored within the AWS Asia Pacific (Sydney) region — your data never leaves Australian jurisdiction.' },
      { label: 'Zero Training on Client Data', value: 'We never use client documents, queries, or workflow outputs to train AI models. Ever.' },
    ],
  },

  /* One line under each tab's name in the sidebar. */
  tabDescriptions: {
    infrastructure: 'AWS Sydney region, physical security, and network isolation.',
    encryption: 'AES-256 at rest, TLS 1.3 in transit, RBAC, and audit logs.',
    compliance: 'Privacy Act 1988, APPs, and AWS-inherited certifications.',
    continuity: 'Automated backups, multi-AZ failover, and incident response.',
    'ai-confidentiality': 'No training on your data; zero-retention LLM agreements.',
  } as Record<string, string>,

  tabs: [
    {
      key: 'infrastructure',
      label: 'Infrastructure & Hosting',
      title: 'Built on AWS — the Global Standard for Secure Cloud',
      subtitle: 'Tier-1 cloud infrastructure with Australian data residency and proven physical security.',
      image: '/images/data-privacy/infrastructure.png',
      intro:
        'JurisTab is hosted entirely on Amazon Web Services (AWS). AWS operates one of the most rigorously secured cloud environments in the world — used by financial institutions, governments, healthcare providers, and law firms globally. By building on AWS, JurisTab inherits decades of investment in physical, network, and operational security that no in-house solution can match.',
      items: [
        {
          title: 'AWS Asia Pacific (Sydney) Region',
          description:
            'All customer data — documents, search history, account information, AI outputs — is stored and processed within the AWS Sydney region (ap-southeast-2). Your data never leaves Australian jurisdiction, meeting sovereignty requirements for legal practitioners under Australian privacy law.',
        },
        {
          title: 'Multi-Availability-Zone Deployment',
          description:
            'Production services run across multiple AWS Availability Zones within Sydney. Each AZ is a physically separate data centre with independent power, cooling, and networking — ensuring continuous availability even if a single facility experiences a disruption.',
        },
        {
          title: 'Physical & Environmental Security',
          description:
            'AWS data centres feature 24/7 armed security, biometric access controls, video surveillance, intrusion detection, fire suppression, and redundant power. AWS audits these controls under SOC 1, SOC 2, SOC 3, ISO 27001, ISO 27017, ISO 27018, and PCI-DSS — JurisTab inherits the benefit of every one.',
        },
        {
          title: 'Network Isolation (VPC)',
          description:
            'JurisTab’s services run inside a dedicated AWS Virtual Private Cloud (VPC) with private subnets, security groups, and network ACLs. Databases and storage are not exposed to the public internet — all access is mediated through authenticated API endpoints behind AWS API Gateway.',
        },
      ],
    },
    {
      key: 'encryption',
      label: 'Encryption & Access',
      title: 'Encryption at Every Layer, Access on a Need-to-Know Basis',
      subtitle: 'Industry-standard encryption, strict identity controls, and full audit logging of every action.',
      image: '/images/data-privacy/encryption.png',
      intro:
        'No data leaves JurisTab unencrypted, and no data sits at rest unencrypted. Access is governed by the principle of least privilege: people and systems get only the permissions they need, only for as long as they need them, and every access is logged.',
      items: [
        {
          title: 'Encryption at Rest (AES-256)',
          description:
            'All customer data — documents in Amazon S3, structured data in PostgreSQL and DynamoDB, search indices, and backups — is encrypted using AES-256, the same standard adopted by governments and financial institutions worldwide. Encryption keys are managed through AWS Key Management Service (KMS) with hardware security modules (HSMs).',
        },
        {
          title: 'Encryption in Transit (TLS 1.3)',
          description:
            'Every connection — between you and JurisTab, between our services, and between JurisTab and AWS APIs — is encrypted using TLS 1.3, the latest and most secure version of the protocol. We enforce HTTPS-only access; downgrades to weaker cipher suites are blocked.',
        },
        {
          title: 'Role-Based Access Control (RBAC)',
          description:
            'Both customers and JurisTab personnel operate under role-based access control. Customer accounts isolate data to the authorised user or organisation. Internal access to production systems is restricted to a small group of engineers, requires multi-factor authentication, and is granted on a least-privilege, time-bound basis.',
        },
        {
          title: 'Comprehensive Audit Logging',
          description:
            'Every administrative action, API call, and data access event is logged through AWS CloudTrail and our application audit pipeline. Logs are stored in tamper-resistant S3 buckets with retention policies aligned to legal-industry expectations and are available for incident review and compliance reporting.',
        },
      ],
    },
    {
      key: 'compliance',
      label: 'Compliance & Standards',
      title: 'Aligned with Australian Privacy Law and Global Standards',
      subtitle: 'Privacy Act 1988, Australian Privacy Principles, and the certifications inherited from AWS.',
      image: '/images/data-privacy/compliance.png',
      intro:
        'JurisTab is designed to meet the obligations Australian legal professionals face under the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). The platform also benefits from the global certifications AWS maintains — covering security, availability, and confidentiality — meaning your data sits within an infrastructure that has been independently audited against the world’s most demanding standards.',
      items: [
        {
          title: 'Privacy Act 1988 & APPs Alignment',
          description:
            'JurisTab’s data handling practices align with the 13 Australian Privacy Principles — covering collection, use, disclosure, data quality, security, access, correction, and cross-border disclosure. Our Privacy Policy details what we collect, why, and your rights as a data subject.',
        },
        {
          title: 'AWS Inherited Certifications',
          description:
            'By hosting on AWS, JurisTab benefits from AWS’s certifications and attestations: ISO 27001 (information security), ISO 27017 (cloud security), ISO 27018 (cloud privacy), SOC 1, SOC 2, SOC 3, PCI-DSS, IRAP (Australian Government), and HIPAA-eligible services. These cover the physical, network, and infrastructure layers your data lives on.',
        },
        {
          title: 'Privacy by Design',
          description:
            'Privacy is built into the platform from the ground up — not retrofitted. We practise data minimisation (collect only what’s necessary), purpose limitation (use data only for the stated purpose), and storage limitation (retain only as long as required). New features pass a privacy impact review before release.',
        },
        {
          title: 'Cross-Border Data Disclosure',
          description:
            'JurisTab does not transfer customer data outside Australia for processing or storage. Where AI processing requires interaction with third-party large language model providers, those interactions are governed by zero-retention and no-training contractual terms — covered in the AI & Confidentiality tab.',
        },
      ],
    },
    {
      key: 'continuity',
      label: 'Backup & Continuity',
      title: 'Resilient by Design, Recoverable by Default',
      subtitle: 'Automated backups, multi-AZ failover, and incident response procedures that keep you operating.',
      image: '/images/data-privacy/continuity.png',
      intro:
        'Data security is not just confidentiality — it is also availability and integrity. JurisTab’s infrastructure is engineered for high availability, with automated backups, multi-zone failover, and a defined incident response process so that disruptions are short, recoveries are clean, and you keep working.',
      items: [
        {
          title: 'Automated, Encrypted Backups',
          description:
            'Databases are backed up automatically with point-in-time recovery enabled. Document storage in S3 uses cross-AZ replication and versioning, so deleted or corrupted files can be recovered. All backups are encrypted with AES-256 and stored within the AWS Sydney region.',
        },
        {
          title: 'High Availability & Failover',
          description:
            'Critical services are deployed across multiple AWS Availability Zones. If one zone experiences an outage, traffic automatically shifts to healthy zones without customer intervention. Defined RTO (Recovery Time Objective) and RPO (Recovery Point Objective) targets govern our disaster recovery posture.',
        },
        {
          title: 'Continuous Monitoring & Alerting',
          description:
            'AWS CloudWatch, security monitoring services, and our application observability stack continuously watch for anomalies — failed logins, unusual data access patterns, performance degradation, and security events. Alerts page on-call engineers within minutes of detection.',
        },
        {
          title: 'Incident Response & Notification',
          description:
            'We maintain a documented incident response procedure covering detection, containment, eradication, recovery, and post-incident review. In the unlikely event of a data incident affecting customer data, we will notify affected customers in line with our obligations under the Notifiable Data Breaches scheme.',
        },
      ],
    },
    {
      key: 'ai-confidentiality',
      label: 'AI & Confidentiality',
      title: 'Your Documents Stay Yours — Including from the AI',
      subtitle: 'Zero training on client data, tenant isolation, and zero-retention agreements with AI providers.',
      image: '/images/data-privacy/ai-confidentiality.png',
      intro:
        'Legal documents are privileged. The most common concern we hear from clients is what happens to documents and queries once AI gets involved — whether they end up training someone’s model, leaking into a future user’s search, or being retained by a third party. The answer at JurisTab is straightforward: they do not.',
      items: [
        {
          title: 'No Training on Client Data',
          description:
            'We do not use customer documents, search queries, AI prompts, or workflow outputs to train, fine-tune, or improve any AI model — neither our own nor any third party’s. Your data is processed to deliver the result you asked for, then handled according to your retention preferences.',
        },
        {
          title: 'Tenant Isolation',
          description:
            'Each customer’s data is logically isolated. Authentication, authorisation, and data access controls ensure one customer’s documents, queries, and AI outputs are never visible to another customer. Workspaces, documents, and search history are scoped to the account that created them.',
        },
        {
          title: 'Zero-Retention LLM Agreements',
          description:
            'Where JurisTab uses third-party large language models (for example, for AI-assisted drafting, contract review, or smart search), those interactions are governed by enterprise agreements that contractually prohibit the provider from retaining, logging, or training on your data. Prompts and responses are processed in memory and discarded.',
        },
        {
          title: 'Provenance & Verifiability',
          description:
            'Every AI-generated output — case summaries, citations, extracted clauses — includes provenance indicators so you can trace it back to the source document or authority. This is both a confidentiality control (no fabricated quotes) and a professional-practice control (you can always verify before acting).',
        },
      ],
    },
  ],

  faq: {
    label: 'Common Questions',
    title: 'What Clients Most Often Ask',
    items: [
      {
        question: 'Where exactly is our data stored?',
        answer:
          'Within the AWS Asia Pacific (Sydney) region — physically inside Australian data centres operated by AWS. Your data does not leave Australian jurisdiction for storage or processing.',
      },
      {
        question: 'Can JurisTab staff read our documents?',
        answer:
          'No. Customer data is encrypted at rest and access is restricted to a small group of engineers under strict role-based controls, MFA, and audit logging. Production data is not accessed routinely — only for explicitly authorised support investigations with customer consent or as required by law.',
      },
      {
        question: 'Will our documents be used to train AI?',
        answer:
          'No. We do not train any AI model on customer documents, queries, or outputs. Third-party LLM providers we use are contractually bound to zero-retention and no-training terms.',
      },
      {
        question: 'What happens to our data if we stop using JurisTab?',
        answer:
          'On account closure, customer data is deleted in line with our retention policy, subject to any legal hold or regulatory obligation. Customers can request export of their data prior to closure and may request deletion of personal information under the Privacy Act 1988.',
      },
      {
        question: 'Has JurisTab been independently audited?',
        answer:
          'The AWS infrastructure JurisTab is built on is audited annually against ISO 27001, SOC 1/2/3, PCI-DSS, and IRAP, among others. JurisTab’s application-layer security practices align with these standards and are subject to ongoing internal review and external penetration testing.',
      },
      {
        question: 'What happens if there is a data breach?',
        answer:
          'We follow a documented incident response procedure: detect, contain, eradicate, recover, and review. If a breach is likely to result in serious harm and meets the threshold under the Notifiable Data Breaches scheme, affected customers and the OAIC will be notified in accordance with the Privacy Act 1988.',
      },
    ],
  },

  cta: {
    title: 'Confidence Begins with Clarity',
    subtitle:
      'We believe trust is earned through transparency. If you have specific data security requirements for your firm or organisation, we are happy to walk through them with you.',
    ctaPrimary: 'Contact Our Team',
    ctaPrimaryLink: '/contact',
    ctaSecondary: 'View Privacy Policy',
    ctaSecondaryLink: '/privacy-policy',
  },
};

/* ── Privacy Policy page ── */

export const PRIVACY_POLICY = {
  hero: {
    label: 'Privacy Policy',
    title: 'Privacy Policy',
    subtitle: 'How we collect, use, and protect your personal information.',
  },

  lastUpdated: '19 October 2026',

  sections: [
    {
      title: 'Introduction',
      content:
        'www.juristab.com website is owned by JurisTab, which is a data controller of your personal data.\n\nWe have adopted this Privacy Policy, which determines how we are processing the information collected by www.juristab.com, which also provides the reasons why we must collect certain personal data about you. Therefore, you must read this Privacy Policy before using www.juristab.com website.\n\nWe take care of your personal data and undertake to guarantee its confidentiality and security.',
    },
    {
      title: 'Personal Information We Collect',
      content:
        'When you visit the www.juristab.com, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the installed cookies on your device. Additionally, as you browse the Site, we collect information about the individual web pages or products you view, what websites or search terms referred you to the Site, and how you interact with the Site. We refer to this automatically-collected information as "Device Information." Moreover, we might collect the personal data you provide to us (including but not limited to Name, Surname, Address, payment information, etc.) during registration to be able to fulfill the agreement.',
    },
    {
      title: 'Why Do We Process Your Data?',
      content:
        'Our top priority is customer data security, and, as such, we may process only minimal user data, only as much as it is absolutely necessary to maintain the website. Information collected automatically is used only to identify potential cases of abuse and establish statistical information regarding website usage. This statistical information is not otherwise aggregated in such a way that it would identify any particular user of the system.\n\nYou can visit the website without telling us who you are or revealing any information, by which someone could identify you as a specific, identifiable individual. If, however, you wish to use some of the website\'s features, or you wish to receive our newsletter or provide other details by filling a form, you may provide personal data to us, such as your email, first name, last name, city of residence, organization, telephone number. You can choose not to provide us with your personal data, but then you may not be able to take advantage of some of the website\'s features. For example, you won\'t be able to receive our Newsletter or contact us directly from the website. Users who are uncertain about what information is mandatory are welcome to contact us via contact@juristab.com.',
    },
    {
      title: 'Your Rights',
      content:
        'If you are a European resident, you have the following rights related to your personal data:',
      list: [
        'The right to be informed.',
        'The right of access.',
        'The right to rectification.',
        'The right to erasure.',
        'The right to restrict processing.',
        'The right to data portability.',
        'The right to object.',
        'Rights in relation to automated decision-making and profiling.',
      ],
      afterList:
        'If you would like to exercise this right, please contact us through the contact information below.\n\nAdditionally, if you are a European resident, we note that we are processing your information in order to fulfill contracts we might have with you (for example, if you make an order through the Site), or otherwise to pursue our legitimate business interests listed above. Additionally, please note that your information might be transferred outside of Europe, including Canada and the United States.',
    },
    {
      title: 'Connected Email Accounts (Gmail, Outlook and Other Mailboxes)',
      content:
        'JurisTab Case Management lets each user connect their own email account so that correspondence with their clients and other contacts is filed on the right matter. You choose to connect an account, and you can disconnect it at any time from the intake inbox in JurisTab. A Gmail or Outlook account is connected through Google\'s or Microsoft\'s own sign-in page, where you see and approve the access requested; JurisTab never sees your Google or Microsoft password. Other mailboxes are connected with an app password you provide.\n\nWhen you connect an account, JurisTab reads it as follows:',
      list: [
        'Access is read-only. For Gmail we request only the gmail.readonly permission and for Outlook only Mail.Read (with User.Read to identify the account and offline_access to keep it connected). JurisTab never sends, changes, moves or deletes your email.',
        'We read the sender, recipients, date and subject of messages in your mailbox, from the period you choose, to find the emails that involve one of your firm\'s contacts in JurisTab. Messages that involve none of your contacts are not stored.',
        'For a matching email we store its sender, recipients, date, subject and text, and file it on the matter it belongs to, where it is visible to the people working on that matter. For its attachments we keep only their names, types and sizes; an attachment is downloaded from your mailbox and stored on the matter only when someone working on the matter chooses to save it there.',
        'If your firm records time in JurisTab, an email filed on a matter adds a draft time entry, described by its subject and the other person\'s name, to your own time review. Nothing is recorded or billed until you confirm it.',
        'The sign-in token that keeps your account connected, or your app password, is encrypted with a dedicated encryption key and used only by the sync that reads your mailbox.',
        'We use this data only to provide the email filing feature you turned on. We do not use it for advertising, we do not sell it, and we do not use it to develop, improve or train artificial intelligence or machine learning models. No person at JurisTab reads it, except with your permission, where needed for security purposes or to investigate abuse, or to comply with the law.',
        'We do not transfer it to anyone else, except as needed to run the service (our hosting and infrastructure providers, under confidentiality and security obligations), to comply with the law, or as part of a merger or acquisition with notice to you.',
      ],
      afterList:
        'Disconnecting an account stops all reading at once, deletes the stored sign-in token or password and any emails still waiting to be filed, and revokes JurisTab\'s access with Google. Emails already filed on a matter remain part of that matter\'s record until the matter is deleted; you can ask us to delete them by contacting contact@juristab.com. You can also remove JurisTab\'s access at any time from your Google Account (myaccount.google.com/permissions) or your Microsoft account settings.\n\nJurisTab\'s use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy (https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements. Our use of data received from Microsoft APIs follows the Microsoft APIs Terms of Use.',
    },
    {
      title: 'Links to Other Websites',
      content:
        'Our website may contain links to other websites that are not owned or controlled by us. Please be aware that we are not responsible for such other websites or third parties\' privacy practices. We encourage you to be aware when you leave our website and read the privacy statements of each website that may collect personal information.',
    },
    {
      title: 'Information Security',
      content:
        'We secure information you provide on computer servers in a controlled, secure environment, protected from unauthorized access, use, or disclosure. We keep reasonable administrative, technical, and physical safeguards to protect against unauthorized access, use, modification, and personal data disclosure in its control and custody. However, no data transmission over the Internet or wireless network can be guaranteed.',
    },
    {
      title: 'Legal Disclosure',
      content:
        'We will disclose any information we collect, use or receive if required or permitted by law, such as to comply with a subpoena or similar legal process, and when we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.',
    },
    {
      title: 'Contact Information',
      content:
        'If you would like to contact us to understand more about this Policy or wish to contact us concerning any matter relating to individual rights and your Personal Information, you may send an email to contact@juristab.com.',
    },
  ],
};

/* ── Terms & Conditions page ── */

export const TERMS_AND_CONDITIONS = {
  hero: {
    label: 'Terms & Conditions',
    title: 'Terms & Conditions',
    subtitle: 'The rules and regulations for the use of JurisTab\'s website.',
  },

  lastUpdated: '19 October 2026',

  sections: [
    {
      title: 'Welcome',
      content:
        'Welcome to JurisTab!\n\nThese terms and conditions outline the rules and regulations for the use of JurisTab\'s Website, located at https://juristab.com.\n\nBy accessing this website, we assume you accept these terms and conditions. Do not continue to use JurisTab if you do not agree to take all of the terms and conditions stated on this page.',
    },
    {
      title: 'Cookies',
      content:
        'The website uses cookies to help personalize your online experience. By accessing JurisTab, you agreed to use the required cookies.\n\nA cookie is a text file that is placed on your hard disk by a web page server. Cookies cannot be used to run programs or deliver viruses to your computer. Cookies are uniquely assigned to you and can only be read by a web server in the domain that issued the cookie to you.\n\nWe may use cookies to collect, store, and track information for statistical or marketing purposes to operate our website. You have the ability to accept or decline optional Cookies. There are some required Cookies that are necessary for the operation of our website. These cookies do not require your consent as they always work. Please keep in mind that by accepting required Cookies, you also accept third-party Cookies, which might be used via third-party provided services if you use such services on our website, for example, a video display window provided by third parties and integrated into our website.',
    },
    {
      title: 'License',
      content:
        'Unless otherwise stated, JurisTab and/or its licensors own the intellectual property rights for all material on JurisTab. All intellectual property rights are reserved. You may access this from JurisTab for your own personal use subjected to restrictions set in these terms and conditions.\n\nYou must not:',
      list: [
        'Copy or republish material from JurisTab',
        'Sell, rent, or sub-license material from JurisTab',
        'Reproduce, duplicate or copy material from JurisTab',
        'Redistribute content from JurisTab',
      ],
      afterList:
        'This Agreement shall begin on the date hereof.\n\nParts of this website offer users an opportunity to post and exchange opinions and information in certain areas of the website. JurisTab does not filter, edit, publish or review Comments before their presence on the website. Comments do not reflect the views and opinions of JurisTab, its agents, and/or affiliates. Comments reflect the views and opinions of the person who posts their views and opinions. To the extent permitted by applicable laws, JurisTab shall not be liable for the Comments or any liability, damages, or expenses caused and/or suffered as a result of any use of and/or posting of and/or appearance of the Comments on this website.\n\nJurisTab reserves the right to monitor all Comments and remove any Comments that can be considered inappropriate, offensive, or causes breach of these Terms and Conditions.\n\nYou warrant and represent that:',
      secondList: [
        'You are entitled to post the Comments on our website and have all necessary licenses and consents to do so;',
        'The Comments do not invade any intellectual property right, including without limitation copyright, patent, or trademark of any third party;',
        'The Comments do not contain any defamatory, libelous, offensive, indecent, or otherwise unlawful material, which is an invasion of privacy.',
        'The Comments will not be used to solicit or promote business or custom or present commercial activities or unlawful activity.',
      ],
      afterSecondList:
        'You hereby grant JurisTab a non-exclusive license to use, reproduce, edit and authorize others to use, reproduce and edit any of your Comments in any and all forms, formats, or media.',
    },
    {
      title: 'Hyperlinking to Our Content',
      content:
        'The following organizations may link to our Website without prior written approval:',
      list: [
        'Government agencies;',
        'Search engines;',
        'News organizations;',
        'Online directory distributors may link to our Website in the same manner as they hyperlink to the Websites of other listed businesses; and',
        'System-wide Accredited Businesses except soliciting non-profit organizations, charity shopping malls, and charity fundraising groups which may not hyperlink to our Web site.',
      ],
      afterList:
        'These organizations may link to our home page, to publications, or to other Website information so long as the link: (a) is not in any way deceptive; (b) does not falsely imply sponsorship, endorsement, or approval of the linking party and its products and/or services; and (c) fits within the context of the linking party\'s site.\n\nWe may consider and approve other link requests from the following types of organizations:',
      secondList: [
        'Commonly-known consumer and/or business information sources;',
        'Dot.com community sites;',
        'Associations or other groups representing charities;',
        'Online directory distributors;',
        'Internet portals;',
        'Accounting, law, and consulting firms; and',
        'Educational institutions and trade associations.',
      ],
      afterSecondList:
        'We will approve link requests from these organizations if we decide that: (a) the link would not make us look unfavorably to ourselves or to our accredited businesses; (b) the organization does not have any negative records with us; (c) the benefit to us from the visibility of the hyperlink compensates the absence of JurisTab; and (d) the link is in the context of general resource information.\n\nThese organizations may link to our home page so long as the link: (a) is not in any way deceptive; (b) does not falsely imply sponsorship, endorsement, or approval of the linking party and its products or services; and (c) fits within the context of the linking party\'s site.\n\nIf you are one of the organizations listed above and are interested in linking to our website, you must inform us by sending an e-mail to JurisTab. Please include your name, your organization name, contact information as well as the URL of your site, a list of any URLs from which you intend to link to our Website, and a list of the URLs on our site to which you would like to link. Wait 2-3 weeks for a response.\n\nApproved organizations may hyperlink to our Website as follows:',
      thirdList: [
        'By use of our corporate name; or',
        'By use of the uniform resource locator being linked to; or',
        'Using any other description of our Website being linked to that makes sense within the context and format of content on the linking party\'s site.',
      ],
      afterThirdList:
        'No use of JurisTab\'s logo or other artwork will be allowed for linking absent a trademark license agreement.',
    },
    {
      title: 'Content Liability',
      content:
        'We shall not be held responsible for any content that appears on your Website. You agree to protect and defend us against all claims that are raised on your Website. No link(s) should appear on any Website that may be interpreted as libelous, obscene, or criminal, or which infringes, otherwise violates, or advocates the infringement or other violation of, any third party rights.',
    },
    {
      title: 'Reservation of Rights',
      content:
        'We reserve the right to request that you remove all links or any particular link to our Website. You approve to immediately remove all links to our Website upon request. We also reserve the right to amend these terms and conditions and its linking policy at any time. By continuously linking to our Website, you agree to be bound to and follow these linking terms and conditions.',
    },
    {
      title: 'Removal of Links from Our Website',
      content:
        'If you find any link on our Website that is offensive for any reason, you are free to contact and inform us at any moment. We will consider requests to remove links, but we are not obligated to or so or to respond to you directly.\n\nWe do not ensure that the information on this website is correct. We do not warrant its completeness or accuracy, nor do we promise to ensure that the website remains available or that the material on the website is kept up to date.',
    },
    {
      title: 'Connected Email Accounts',
      content:
        'If you connect an email account to JurisTab Case Management, you confirm that it is your account or that you are authorised to connect it, and that your firm permits it. JurisTab reads the account read-only, keeps only emails that involve your firm\'s contacts, and files them on matters as described in our Privacy Policy (https://www.juristab.com/privacy-policy). It never sends, changes or deletes your email.\n\nYou are responsible for checking that emails are filed on the right matter, and for who in your firm can see the matters they are filed on. You can disconnect the account at any time, which stops all reading and deletes the stored sign-in; emails already filed stay on their matter. Your use of Gmail or Outlook remains subject to Google\'s and Microsoft\'s own terms.',
    },
    {
      title: 'Disclaimer',
      content:
        'To the maximum extent permitted by applicable law, we exclude all representations, warranties, and conditions relating to our website and the use of this website. Nothing in this disclaimer will:',
      list: [
        'Limit or exclude our or your liability for death or personal injury;',
        'Limit or exclude our or your liability for fraud or fraudulent misrepresentation;',
        'Limit any of our or your liabilities in any way that is not permitted under applicable law; or',
        'Exclude any of our or your liabilities that may not be excluded under applicable law.',
      ],
      afterList:
        'The limitations and prohibitions of liability set in this Section and elsewhere in this disclaimer: (a) are subject to the preceding paragraph; and (b) govern all liabilities arising under the disclaimer, including liabilities arising in contract, in tort, and for breach of statutory duty.\n\nAs long as the website and the information and services on the website are provided free of charge, we will not be liable for any loss or damage of any nature.',
    },
  ],
};

/* ── Practice Areas page ── */

export const PRACTICE_AREAS = {
  hero: {
    label: 'Practice Areas',
    title: 'AI-Powered Legal Research for Every Practice Area',
    subtitle:
      'Discover how JurisTab transforms legal research across Australia\u2019s key practice areas \u2014 and the dedicated workflow suites built for family, criminal and property practice.',
  },

  intro: {
    label: 'Why Practice-Specific Research Matters',
    title: 'Every Practice Area Has Unique Research Demands',
    text: 'A family lawyer searching for parenting order precedents faces entirely different challenges than a commercial litigator tracing contractual interpretation across Federal Court decisions. JurisTab\u2019s AI understands the nuances of each domain \u2014 surfacing the most relevant cases, legislation, and judicial reasoning specific to your area of practice.',
  },

  areas: [
    {
      id: 'corporate-commercial',
      title: 'Corporate & Commercial Law',
      shortDesc: 'Mergers, acquisitions, compliance, and corporate governance research.',
      icon: 'FaBriefcase',
      colour: '#4A90D9',
      overview:
        'Corporate and commercial lawyers navigate a complex web of legislation including the Corporations Act 2001 (Cth), ASIC regulatory guidance, and evolving case law on directors\u2019 duties, shareholder disputes, and insolvency. The pace of regulatory change means staying current is critical.',
      stats: [
        { value: '12,400+', label: 'Corporate law cases indexed on JurisTab' },
        { value: '47%', label: 'Of ASIC enforcement actions in 2023\u201324 related to directors\u2019 duties breaches' },
        { value: '3.2x', label: 'Faster precedent discovery vs manual research (avg. user-reported)' },
      ],
      benefits: [
        {
          title: 'Instant Directors\u2019 Duties Analysis',
          description:
            'Search "breach of directors\u2019 duties investment decision" and JurisTab returns ranked results across Federal Court and state Supreme Courts, with Brief extracting the key ratio from cases like ASIC v Healey [2011] FCA 717 in seconds.',
        },
        {
          title: 'Track Regulatory Shifts in Real Time',
          description:
            'JurisLink maps how amendments to the Corporations Act flow through to judicial interpretation. When ASIC releases new guidance on ESG disclosure obligations, see instantly which cases have applied or distinguished the prior standard.',
        },
        {
          title: 'M&A Due Diligence Support',
          description:
            'Use JurisLink to trace the citation lineage of key warranty and indemnity clauses through Australian M&A case law. Identify how courts have interpreted "material adverse change" clauses across 200+ relevant decisions.',
        },
      ],
      exampleQuery: '"oppression remedy minority shareholder" Corporations Act s232',
    },
    {
      id: 'criminal-law',
      title: 'Criminal Law & Defence',
      shortDesc: 'Criminal defence, prosecution research, and sentencing precedents.',
      icon: 'FaGavel',
      colour: '#D94A4A',
      overview:
        'Criminal law practitioners must navigate Commonwealth and state criminal codes, the Evidence Act 1995, bail legislation, and a vast body of sentencing precedent. With Australian courts handling over 600,000 criminal matters annually, finding the right precedent at the right time is essential for effective advocacy.',
      stats: [
        { value: '85,000+', label: 'Criminal law decisions indexed across all Australian jurisdictions' },
        { value: '68%', label: 'Of criminal appeals cite sentencing comparability \u2014 JurisTab maps these automatically' },
        { value: '< 5 sec', label: 'Average time to surface comparable sentencing decisions' },
      ],
      benefits: [
        {
          title: 'Sentencing Comparability Research',
          description:
            'Search "sentencing drug supply commercial quantity" and JurisTab surfaces comparable sentences across jurisdictions, with Brief extracting the key sentencing factors from cases like R v Wong [2001] HCA 64 and state equivalents.',
        },
        {
          title: 'Evidence Admissibility Analysis',
          description:
            'Quickly find how courts have ruled on specific evidence types. Search "admissibility of covert recordings Evidence Act s138" to see judicial treatment of illegally obtained evidence, with full reasoning chains.',
        },
        {
          title: 'Bail Application Precedents',
          description:
            'Appearances visualises the parties and procedural history in complex multi-accused matters. Build bail applications using comparable grants from the same court, matching offence type and circumstances.',
        },
      ],
      exampleQuery: '"self-defence excessive force" Crimes Act intoxication',
      suite: {
        count: '13 services',
        desc: 'Each run is set to the jurisdiction of the offence \u2014 any state or territory, or a Commonwealth offence.',
        services: [
          'Brief of Evidence Review', 'Charge Analysis', 'Witness Inconsistency Check', 'Record of Interview Review',
          'Evidence Objections Schedule', 'Criminal History Analysis', 'Criminal Chronology', 'Bail Application',
          'Plea in Mitigation', 'Disclosure Request', 'Representations to the Prosecution',
          'Traffic & Drink-Driving Assessment', 'Client-Friendly Explanation (Criminal)',
        ],
      },
    },
    {
      id: 'family-law',
      title: 'Family Law',
      shortDesc: 'Parenting orders, property settlements, and domestic violence matters.',
      icon: 'FaHome',
      colour: '#D9A04A',
      overview:
        'Family law practitioners deal with the Family Law Act 1975 (Cth), state domestic violence legislation, and child protection laws. With the Federal Circuit and Family Court of Australia processing over 100,000 applications annually, practitioners need fast access to relevant precedent on parenting, property, and financial matters.',
      stats: [
        { value: '28,000+', label: 'Family law decisions searchable on JurisTab' },
        { value: '62%', label: 'Of parenting disputes cite "best interests" factors \u2014 JurisTab categorises all relevant criteria' },
        { value: '4.1x', label: 'Faster to identify relevant property settlement precedents' },
      ],
      benefits: [
        {
          title: 'Parenting Order Research',
          description:
            'Search "relocation interstate primary carer" and JurisTab returns decisions ranked by relevance, with Brief extracting how courts weighed factors under s 60CC of the Family Law Act. See how cases like AMS v AIF [1999] HCA 26 have shaped the framework.',
        },
        {
          title: 'Property Settlement Analysis',
          description:
            'Trace the four-step process from Hickey v Hickey [2003] FamCA 395 through subsequent decisions. JurisLink shows how courts have applied and refined the approach across different asset pools and contributions.',
        },
        {
          title: 'Domestic Violence Precedents',
          description:
            'JurisLink connects family violence findings across Family Court and state Magistrates\u2019 Court decisions, helping practitioners build comprehensive submissions on the impact of family violence on parenting arrangements.',
        },
      ],
      exampleQuery: '"spousal maintenance" earning capacity health "s 75(2)"',
      suite: {
        count: '14 services',
        desc: 'Every line of output links to its source page, and figures such as the asset pool are calculated in code.',
        services: [
          'Disclosure Cross-Check', 'Asset Pool Schedule', 'Chronology Builder', 'Affidavit Draft',
          'Consent Orders & Minutes', 'Court Book Index', 'Subpoena Material Review', 'Family Violence & Risk Screening',
          'Family Law Correspondence', 'Financial Agreement Review', 'Business & Trust Analysis',
          'Superannuation Splitting Check', 'Client-Friendly Explanation', 'Offer & Costs Analysis',
        ],
      },
    },
    {
      id: 'property-real-estate',
      title: 'Property & Real Estate Law',
      shortDesc: 'Conveyancing, planning disputes, leasing, and land title issues.',
      icon: 'FaBuilding',
      colour: '#4AD98A',
      overview:
        'Property lawyers work across state-based Torrens title systems, planning and environment legislation, retail leases acts, and strata schemes. With property transactions worth over $380 billion annually in Australia, the stakes of getting legal research right are immense.',
      stats: [
        { value: '18,500+', label: 'Property and conveyancing decisions indexed' },
        { value: '$380B+', label: 'Annual Australian property transactions \u2014 research accuracy is critical' },
        { value: '2.8x', label: 'Faster lease dispute precedent identification' },
      ],
      benefits: [
        {
          title: 'Torrens Title & Indefeasibility Research',
          description:
            'Search "indefeasibility of title fraud exception" and JurisTab traces the lineage from Frazer v Walker [1967] 1 AC 569 through to modern Australian applications, with JurisLink showing how each state\u2019s courts have treated the exception differently.',
        },
        {
          title: 'Lease Dispute Analysis',
          description:
            'Brief distils complex retail lease disputes into key findings on rent review, outgoings, and make-good obligations. Compare how VCAT, NCAT, and QCAT have approached similar provisions across different state Retail Leases Acts.',
        },
        {
          title: 'Planning & Development Precedents',
          description:
            'Use JurisLink to map how planning decisions in one jurisdiction influence outcomes in another. Track how courts have interpreted "amenity impact" and "neighbourhood character" across hundreds of planning appeal decisions.',
        },
      ],
      exampleQuery: '"caveatable interest" constructive trust property Torrens',
      suite: {
        count: '4 services',
        desc: 'Contracts of sale reviewed with their vendor disclosure, title reported on, and the answer explained for the client.',
        services: [
          'Property Contract & Disclosure Review', 'Title & Encumbrance Memo', 'Property Law Research', 'Plain-Language Explainer',
        ],
      },
    },
    {
      id: 'employment-workplace',
      title: 'Employment & Workplace Law',
      shortDesc: 'Unfair dismissal, enterprise bargaining, and workplace safety.',
      icon: 'FaUsers',
      colour: '#9B59B6',
      overview:
        'Employment lawyers navigate the Fair Work Act 2009, state WHS legislation, anti-discrimination laws, and a high volume of Fair Work Commission decisions. With the FWC receiving over 14,000 unfair dismissal applications in 2023\u201324 alone, staying across the latest trends in workplace law is essential.',
      stats: [
        { value: '42,000+', label: 'Employment and workplace decisions indexed' },
        { value: '14,000+', label: 'Unfair dismissal applications to FWC in 2023\u201324 \u2014 all indexed' },
        { value: '71%', label: 'Of unfair dismissal cases settle \u2014 JurisTab helps calibrate settlement ranges' },
      ],
      benefits: [
        {
          title: 'Unfair Dismissal Research',
          description:
            'Search "unfair dismissal valid reason misconduct social media" and JurisTab surfaces FWC decisions where social media conduct was the basis for termination. Brief extracts the key factors the Commission weighed, including cases like Fitzgerald v Smith [2010] FWA 7358.',
        },
        {
          title: 'Enterprise Agreement Interpretation',
          description:
            'JurisLink maps how specific enterprise agreement clauses have been interpreted across FWC and Federal Court decisions. Track how courts have applied the principles from WorkPac Pty Ltd v Rossato [2021] HCA 23 on casual employment.',
        },
        {
          title: 'Workplace Safety Prosecution Precedents',
          description:
            'Research WHS penalty trends with JurisTab\u2019s data. Compare penalties for Category 1, 2, and 3 offences across jurisdictions, with JurisLink showing how sentencing principles from SafeWork NSW v X have been applied in subsequent prosecutions.',
        },
      ],
      exampleQuery: '"adverse action" general protections "workplace right" complaint',
    },
    {
      id: 'immigration-law',
      title: 'Immigration Law',
      shortDesc: 'Visa applications, refugee claims, and migration tribunal decisions.',
      icon: 'FaGlobe',
      colour: '#E67E22',
      overview:
        'Immigration practitioners work with the Migration Act 1958 (Cth), Migration Regulations, and a high volume of Administrative Appeals Tribunal (AAT) and Federal Court decisions. With over 8 million visa applications processed annually, the body of relevant case law is vast and constantly evolving.',
      stats: [
        { value: '35,000+', label: 'Migration and refugee decisions indexed' },
        { value: '8M+', label: 'Visa applications processed annually in Australia' },
        { value: '5.2x', label: 'Faster identification of relevant AAT precedents' },
      ],
      benefits: [
        {
          title: 'Visa Refusal & Cancellation Research',
          description:
            'Search "character test s501 visa cancellation ministerial direction" and JurisTab returns Federal Court and AAT decisions ranked by relevance. Brief extracts how decision-makers weighed primary and other considerations under Ministerial Direction 99.',
        },
        {
          title: 'Refugee Claim Precedents',
          description:
            'Track how the "well-founded fear of persecution" test from Chan v Minister for Immigration [1989] HCA 62 has been applied to specific country conditions and particular social groups. JurisLink maps the evolution of complementary protection under s 36(2)(aa).',
        },
        {
          title: 'Procedural Fairness in Migration',
          description:
            'JurisLink connects jurisdictional error findings across the Federal Circuit Court and Federal Court, helping identify patterns in successful judicial review applications. Surface cases where procedural fairness breaches led to remittal.',
        },
      ],
      exampleQuery: '"partner visa" "genuine relationship" s 5F Migration Regulations',
    },
    {
      id: 'personal-injury',
      title: 'Personal Injury & Insurance Law',
      shortDesc: 'Motor vehicle accidents, medical negligence, and workers\u2019 compensation.',
      icon: 'FaHeartbeat',
      colour: '#E74C3C',
      overview:
        'Personal injury lawyers operate across state-based civil liability legislation, motor accident schemes, workers\u2019 compensation regimes, and medical negligence frameworks. Each state has different thresholds, caps, and procedural requirements. JurisTab helps practitioners navigate this complexity efficiently.',
      stats: [
        { value: '22,000+', label: 'Personal injury and insurance decisions indexed' },
        { value: '$4.2B', label: 'Paid annually in Australian workers\u2019 compensation claims' },
        { value: '3.6x', label: 'Faster to find comparable quantum decisions' },
      ],
      benefits: [
        {
          title: 'Damages Quantum Research',
          description:
            'Search "general damages spinal injury L4-L5 disc" and JurisTab returns comparable quantum assessments across jurisdictions. Brief extracts the injury description, award breakdown, and how courts applied the relevant Civil Liability Act caps.',
        },
        {
          title: 'Medical Negligence Analysis',
          description:
            'Trace the application of the Bolam test and its Australian refinement through Rogers v Whitaker [1992] HCA 58. JurisLink shows how courts have applied the standard of care across different medical specialties and treatment decisions.',
        },
        {
          title: 'Workers\u2019 Compensation Precedents',
          description:
            'Compare how different state tribunals and courts have interpreted "arising out of or in the course of employment" for specific injury types. JurisLink maps the interaction between workers\u2019 compensation schemes and common law claims.',
        },
      ],
      exampleQuery: '"whole person impairment" threshold "non-economic loss" motor accident',
    },
    {
      id: 'environmental-planning',
      title: 'Environmental & Planning Law',
      shortDesc: 'Environmental protection, development approvals, and heritage matters.',
      icon: 'FaTree',
      colour: '#27AE60',
      overview:
        'Environmental and planning lawyers work across the Environment Protection and Biodiversity Conservation Act 1999 (Cth), state planning legislation, and local government instruments. With major infrastructure projects and climate-related litigation increasing, this is one of the fastest-growing areas of legal practice.',
      stats: [
        { value: '15,000+', label: 'Environmental and planning decisions indexed' },
        { value: '340%', label: 'Increase in climate-related litigation in Australia since 2017' },
        { value: '2.9x', label: 'Faster to research development approval precedents' },
      ],
      benefits: [
        {
          title: 'EPBC Act Research',
          description:
            'Search "controlled action significant impact matters of national environmental significance" and JurisTab surfaces Federal Court decisions on referral triggers and assessment processes. Track how courts have interpreted "significant impact" since the Tarkine cases.',
        },
        {
          title: 'Climate Litigation Precedents',
          description:
            'JurisLink maps the growing body of Australian climate litigation from Sharma v Minister for the Environment [2022] FCAFC 35 through corporate climate risk disclosure cases. See how duty of care arguments are evolving in this space.',
        },
        {
          title: 'Development Approval Appeals',
          description:
            'Compare how the Land and Environment Court (NSW), VCAT, and Planning and Environment Court (QLD) have approached similar development assessment criteria. Brief extracts the key planning grounds from hundreds of appeal decisions.',
        },
      ],
      exampleQuery: '"environmental impact statement" biodiversity offset "threatened species"',
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Law',
      shortDesc: 'Patents, trademarks, copyright, and trade secrets litigation.',
      icon: 'FaLightbulb',
      colour: '#F39C12',
      overview:
        'IP practitioners navigate the Patents Act 1990, Trade Marks Act 1995, Copyright Act 1968, and evolving case law on digital rights, AI-generated content, and trade secrets. With IP Australia processing over 80,000 trade mark applications annually, the intersection of statutory and common law IP protection is complex.',
      stats: [
        { value: '9,800+', label: 'Intellectual property decisions indexed' },
        { value: '80,000+', label: 'Trade mark applications filed with IP Australia annually' },
        { value: '4.3x', label: 'Faster to trace patent claim construction precedents' },
      ],
      benefits: [
        {
          title: 'Trade Mark Infringement Research',
          description:
            'Search "deceptively similar trade mark likelihood of confusion" and JurisTab returns Federal Court and ATMO decisions. Brief extracts the key factors from cases like Registrar of Trade Marks v Woolworths Ltd [1999] HCA 19, showing how "deceptively similar" has been interpreted across industries.',
        },
        {
          title: 'Patent Validity & Construction',
          description:
            'JurisLink maps the citation lineage of patent construction principles from Kimberly-Clark v Arico [2001] HCA 8 through subsequent Federal Court applications. Track how courts have approached claims construction, novelty, and inventive step for specific technology areas.',
        },
        {
          title: 'Copyright in the Digital Age',
          description:
            'Research how Australian courts have approached copyright in digital content, AI-generated works, and platform liability. JurisLink connects Copyright Act provisions to judicial interpretation, including landmark decisions on fair dealing and safe harbour protections.',
        },
      ],
      exampleQuery: '"passing off" "misleading or deceptive conduct" trade mark goodwill',
    },
    {
      id: 'tax-law',
      title: 'Tax Law',
      shortDesc: 'Income tax, GST, transfer pricing, and ATO dispute resolution.',
      icon: 'FaCalculator',
      colour: '#2C3E50',
      overview:
        'Tax practitioners must navigate the Income Tax Assessment Act 1936 and 1997, A New Tax System (Goods and Services Tax) Act 1999, and a vast body of ATO rulings and Federal Court decisions. With the ATO collecting over $600 billion in revenue annually, the stakes of accurate tax research are significant.',
      stats: [
        { value: '16,500+', label: 'Tax law decisions indexed across all courts and tribunals' },
        { value: '$600B+', label: 'Annual ATO revenue collection \u2014 accuracy in tax research is paramount' },
        { value: '3.8x', label: 'Faster to research Part IVA anti-avoidance precedents' },
      ],
      benefits: [
        {
          title: 'Anti-Avoidance (Part IVA) Research',
          description:
            'Search "Part IVA tax benefit scheme dominant purpose" and JurisTab returns Federal Court and High Court decisions. Brief extracts how courts have applied the eight factors in s 177D, including landmark cases like FCT v Spotless Services [1996] HCA 34.',
        },
        {
          title: 'GST Classification Disputes',
          description:
            'JurisLink maps how the AAT and Federal Court have classified supplies for GST purposes across different industries. Track the treatment of mixed supplies, financial supplies, and input tax credit entitlements through JurisLink\u2019s citation hierarchy.',
        },
        {
          title: 'Transfer Pricing Analysis',
          description:
            'Research how Australian courts have applied the arm\u2019s length principle under Division 815, including comparability analysis and profit allocation methods. Brief distils complex transfer pricing decisions into actionable precedent summaries.',
        },
      ],
      exampleQuery: '"assessable income" "ordinary income" s 6-5 ITAA 1997 "economic benefit"',
    },
    {
      id: 'banking-finance',
      title: 'Banking & Finance Law',
      shortDesc: 'Lending, securities regulation, fintech compliance, and financial disputes.',
      icon: 'FaUniversity',
      colour: '#1ABC9C',
      overview:
        'Banking and finance lawyers advise on the National Consumer Credit Protection Act 2009, Banking Act 1959, Corporations Act financial services provisions, and APRA/ASIC regulatory frameworks. With Australia\u2019s financial services sector managing over $4.5 trillion in assets and fintech disruption accelerating, regulatory compliance and dispute resolution are increasingly complex.',
      stats: [
        { value: '14,200+', label: 'Banking and finance decisions indexed on JurisTab' },
        { value: '$4.5T+', label: 'Assets under management in Australian financial services' },
        { value: '3.5x', label: 'Faster to research responsible lending obligation precedents' },
      ],
      benefits: [
        {
          title: 'Responsible Lending Compliance',
          description:
            'Search "responsible lending obligations unsuitable credit contract" and JurisTab surfaces Federal Court and AFCA decisions on lender conduct. Brief extracts how courts applied the obligations in ASIC v Westpac Banking Corporation [2019] FCAFC 187 and subsequent cases.',
        },
        {
          title: 'Securities & Financial Products',
          description:
            'JurisLink traces the evolution of financial product disclosure obligations from the Corporations Act through ASIC regulatory guides and Federal Court interpretations. Track how "managed investment scheme" definitions have been applied across different investment structures.',
        },
        {
          title: 'Banking Dispute Resolution',
          description:
            'JurisLink connects AFCA determinations, FOS predecessor decisions, and Federal Court authorities on unfair contract terms, unconscionable conduct, and misleading financial advice. Compare outcomes across dispute types and financial products.',
        },
      ],
      exampleQuery: '"unfair contract term" "financial product" ASIC "consumer protection"',
    },
    {
      id: 'construction-infrastructure',
      title: 'Construction & Infrastructure Law',
      shortDesc: 'Building disputes, security of payment, defects, and major project delivery.',
      icon: 'FaHardHat',
      colour: '#E59866',
      overview:
        'Construction lawyers work across state-based security of payment legislation, Australian Standard contracts (AS 4000, AS 2124), Design and Build contracts, and a high volume of adjudication and tribunal decisions. With Australia\u2019s construction industry valued at over $360 billion annually and major infrastructure pipelines across every state, disputes are frequent and high-value.',
      stats: [
        { value: '11,800+', label: 'Construction and building law decisions indexed' },
        { value: '$360B+', label: 'Annual value of Australian construction industry' },
        { value: '4.0x', label: 'Faster adjudication precedent research vs manual methods' },
      ],
      benefits: [
        {
          title: 'Security of Payment Research',
          description:
            'Search "payment claim progress payment Building and Construction Industry" and JurisTab returns adjudication decisions and court authorities across all state SOP Acts. Brief extracts the key validity requirements from cases like Brodyn Pty Ltd v Davenport [2004] NSWCA 394.',
        },
        {
          title: 'Defects & Warranty Claims',
          description:
            'JurisLink maps how courts have interpreted "defective work," "practical completion," and warranty obligations across different standard form contracts. Compare the treatment of latent defects under Home Building Act provisions versus commercial contract regimes.',
        },
        {
          title: 'Delay & Disruption Claims',
          description:
            'Track how Australian courts have applied delay analysis methodologies, liquidated damages clauses, and extension of time provisions. JurisLink traces the citation lineage from Peninsula Balmain v Abigroup [2002] NSWCA 211 through current authorities on concurrent delay.',
        },
      ],
      exampleQuery: '"security of payment" adjudication "payment schedule" "jurisdictional error"',
    },
    {
      id: 'consumer-law',
      title: 'Consumer Law',
      shortDesc: 'ACL protections, unfair terms, product liability, and ACCC enforcement.',
      icon: 'FaShoppingCart',
      colour: '#3498DB',
      overview:
        'Consumer lawyers work with the Australian Consumer Law (Schedule 2 of the Competition and Consumer Act 2010), state fair trading legislation, and ACCC enforcement actions. The ACL provides a nationally consistent framework for consumer guarantees, unfair contract terms, misleading conduct, and product safety \u2014 affecting every business in Australia.',
      stats: [
        { value: '19,500+', label: 'Consumer law and competition decisions indexed' },
        { value: '$230M+', label: 'In ACCC penalties imposed in 2023\u201324 for consumer law breaches' },
        { value: '3.4x', label: 'Faster to research misleading conduct precedents' },
      ],
      benefits: [
        {
          title: 'Misleading or Deceptive Conduct',
          description:
            'Search "misleading or deceptive conduct s 18 ACL representations" and JurisTab returns Federal Court and ACCC enforcement decisions. Brief extracts the elements established in Henjo Investments v Collins Marrickville [1988] FCA 462 and how they\u2019ve been applied across industries.',
        },
        {
          title: 'Unfair Contract Terms',
          description:
            'JurisLink traces the evolution of unfair contract term provisions from the original ASIC Act protections through the 2023 amendments introducing civil penalties. Track how courts have assessed "transparency," "legitimate interest," and the new penalty regime.',
        },
        {
          title: 'Product Liability & Safety',
          description:
            'JurisLink connects product safety recalls, ACCC enforcement actions, and Federal Court decisions on manufacturer liability and consumer guarantees. Compare how courts have applied the "acceptable quality" guarantee across different product categories.',
        },
      ],
      exampleQuery: '"consumer guarantee" "acceptable quality" "major failure" remedy ACL',
    },
    {
      id: 'administrative-government',
      title: 'Administrative & Government Law',
      shortDesc: 'Judicial review, FOI, government decision-making, and regulatory appeals.',
      icon: 'FaLandmark',
      colour: '#8E44AD',
      overview:
        'Administrative lawyers navigate the Administrative Decisions (Judicial Review) Act 1977 (Cth), state administrative tribunal legislation, the Freedom of Information Act 1982, and constitutional law principles governing the exercise of government power. With the Administrative Review Tribunal (ART) replacing the AAT in 2024, the landscape is rapidly evolving.',
      stats: [
        { value: '38,000+', label: 'Administrative law and judicial review decisions indexed' },
        { value: '42,000+', label: 'Applications to the former AAT annually across all divisions' },
        { value: '3.9x', label: 'Faster identification of jurisdictional error grounds' },
      ],
      benefits: [
        {
          title: 'Judicial Review Research',
          description:
            'Search "jurisdictional error unreasonableness Wednesbury" and JurisTab surfaces High Court and Federal Court authorities on grounds of review. Brief extracts how courts have applied the principles from Minister for Immigration v Li [2013] HCA 18 on legal unreasonableness.',
        },
        {
          title: 'Freedom of Information',
          description:
            'JurisLink traces the citation lineage of FOI exemption provisions \u2014 from deliberative process exemptions to personal privacy \u2014 across the Information Commissioner, AAT, and Federal Court. See how exemption claims have been assessed for different document types.',
        },
        {
          title: 'Merits Review & Tribunal Practice',
          description:
            'JurisLink maps the transition from AAT to ART and connects tribunal decisions across divisions (general, migration, social services, veterans). Track how the "correct or preferable decision" standard has been applied in specific regulatory contexts.',
        },
      ],
      exampleQuery: '"procedural fairness" "natural justice" "hearing rule" decision-maker',
    },
    {
      id: 'insolvency-restructuring',
      title: 'Insolvency & Restructuring',
      shortDesc: 'Voluntary administration, liquidation, receivership, and personal bankruptcy.',
      icon: 'FaBalanceScale',
      colour: '#C0392B',
      overview:
        'Insolvency practitioners and lawyers work under Part 5.3A (voluntary administration), Part 5.4 (winding up), and Part X of the Bankruptcy Act 1966. With corporate insolvencies reaching over 11,000 in 2023\u201324 \u2014 the highest since 2015 \u2014 and the introduction of small business restructuring under Part 5.3B, this is an area of intense activity.',
      stats: [
        { value: '13,600+', label: 'Insolvency and bankruptcy decisions indexed' },
        { value: '11,000+', label: 'Corporate insolvencies in 2023\u201324 \u2014 highest in 9 years' },
        { value: '3.7x', label: 'Faster to research voidable transaction precedents' },
      ],
      benefits: [
        {
          title: 'Voidable Transaction Claims',
          description:
            'Search "unfair preference voidable transaction s 588FA" and JurisTab returns Federal Court and state Supreme Court decisions. Brief extracts the elements of unfair preferences from cases like Airservices Australia v Ferrier [1996] HCA 14, including the running account defence.',
        },
        {
          title: 'Directors\u2019 Insolvent Trading',
          description:
            'JurisLink traces the evolution of insolvent trading liability under s 588G, including the safe harbour defence introduced in 2017. Track how courts have assessed "reasonable grounds to suspect insolvency" and what constitutes adequate safe harbour steps.',
        },
        {
          title: 'Small Business Restructuring',
          description:
            'JurisLink connects the emerging body of Part 5.3B small business restructuring decisions, mapping how the simplified restructuring process interacts with existing creditor rights, PPSA priorities, and employee entitlements under FEG/GEERS.',
        },
      ],
      exampleQuery: '"insolvent trading" "safe harbour" s 588GA "reasonable steps"',
    },
    {
      id: 'maritime-admiralty',
      title: 'Maritime & Admiralty Law',
      shortDesc: 'Shipping disputes, cargo claims, maritime liens, and port regulations.',
      icon: 'FaAnchor',
      colour: '#2980B9',
      overview:
        'Maritime lawyers operate under the Admiralty Act 1988, Navigation Act 2012, Marine Safety (Domestic Commercial Vessel) National Law Act 2012, and international conventions including the Hague-Visby Rules and UNCLOS. As one of the world\u2019s largest trading nations by sea freight, with over 1.7 billion tonnes of cargo passing through Australian ports annually, maritime disputes carry significant commercial importance.',
      stats: [
        { value: '4,800+', label: 'Maritime and admiralty decisions indexed' },
        { value: '1.7B+ tonnes', label: 'Annual cargo through Australian ports' },
        { value: '4.5x', label: 'Faster to research in rem arrest precedents' },
      ],
      benefits: [
        {
          title: 'Arrest & In Rem Claims',
          description:
            'Search "arrest of ship maritime lien Admiralty Act" and JurisTab returns Federal Court admiralty decisions. Brief extracts the jurisdictional requirements from cases like The Shin Kobe Maru [1994] HCA 54 and how courts have classified maritime claims under s 4 of the Admiralty Act.',
        },
        {
          title: 'Cargo Claims & Carriage of Goods',
          description:
            'JurisLink traces how Australian courts have applied the amended Hague-Visby Rules through the Carriage of Goods by Sea Act 1991. Track limitation of liability, package limitations, and time bar provisions across different cargo types and shipping routes.',
        },
        {
          title: 'Marine Insurance & Salvage',
          description:
            'JurisLink connects marine insurance disputes, general average claims, and salvage awards across the Federal Court\u2019s admiralty jurisdiction. Research how constructive total loss, particular average, and subrogation principles have been applied in Australian waters.',
        },
      ],
      exampleQuery: '"bill of lading" "carrier liability" Hague-Visby "package limitation"',
    },
    {
      id: 'media-defamation',
      title: 'Media & Defamation Law',
      shortDesc: 'Defamation claims, media privilege, online publication, and reputation disputes.',
      icon: 'FaNewspaper',
      colour: '#D35400',
      overview:
        'Defamation and media lawyers work under the uniform Defamation Acts across Australian states and territories, as substantially amended by the Model Defamation Amendment Provisions 2020. With the rise of social media, online reviews, and digital publication, defamation claims have surged \u2014 Australia has one of the highest per-capita defamation litigation rates in the world.',
      stats: [
        { value: '7,200+', label: 'Defamation and media law decisions indexed' },
        { value: '1,500+', label: 'Defamation proceedings filed annually in Australia' },
        { value: '3.3x', label: 'Faster to research serious harm threshold precedents' },
      ],
      benefits: [
        {
          title: 'Serious Harm Threshold',
          description:
            'Search "serious harm reputation defamation s 10A" and JurisTab surfaces decisions applying the new serious harm element introduced in 2021. Brief extracts how courts have assessed "serious harm" across different plaintiff types \u2014 from individuals to corporations to public officials.',
        },
        {
          title: 'Online Publication & Intermediaries',
          description:
            'JurisLink traces the evolution of publication law from Dow Jones v Gutnick [2002] HCA 56 through to Fairfax Media v Voller [2021] HCA 27 on social media page operator liability. Track how the single publication rule and concerns notices apply to digital content.',
        },
        {
          title: 'Defences & Qualified Privilege',
          description:
            'JurisLink maps how courts have applied the defences of honest opinion, justification, and qualified privilege across different media contexts. Compare the treatment of the public interest defence under s 29A with traditional Lange v ABC qualified privilege.',
        },
      ],
      exampleQuery: '"defamatory imputation" "contextual truth" "public interest" journalism',
    },
    {
      id: 'native-title-indigenous',
      title: 'Native Title & Indigenous Law',
      shortDesc: 'Native title claims, cultural heritage protection, and land use agreements.',
      icon: 'FaMapMarkedAlt',
      colour: '#A0522D',
      overview:
        'Native title lawyers work under the Native Title Act 1993 (Cth), state cultural heritage legislation, and the Aboriginal Land Rights Act 1976 (NT). Since the landmark Mabo v Queensland (No 2) [1992] HCA 23 decision, over 500 native title determinations have been made, covering approximately 40% of Australia\u2019s land mass. The intersection of native title with mining, infrastructure, and cultural heritage protection creates unique legal complexity.',
      stats: [
        { value: '6,500+', label: 'Native title and Indigenous law decisions indexed' },
        { value: '500+', label: 'Native title determinations made since 1993' },
        { value: '40%', label: 'Of Australia\u2019s land mass covered by native title determinations' },
      ],
      benefits: [
        {
          title: 'Native Title Claim Research',
          description:
            'Search "native title determination connection to country traditional laws" and JurisTab returns Federal Court and NNTT decisions. Brief extracts how courts have assessed the connection requirement from Members of the Yorta Yorta v Victoria [2002] HCA 58 through recent consent determinations.',
        },
        {
          title: 'Future Act & ILUA Research',
          description:
            'JurisLink traces how the future act regime in Part 3 Division 3 has been applied to mining tenements, infrastructure projects, and compulsory acquisitions. Track the negotiation and registration of Indigenous Land Use Agreements (ILUAs) across different agreement types.',
        },
        {
          title: 'Cultural Heritage Protection',
          description:
            'JurisLink connects decisions across the Aboriginal and Torres Strait Islander Heritage Protection Act 1984 (Cth) and state cultural heritage regimes. Research how cultural heritage management plans, duty of care obligations, and sacred site protections interact with development approvals.',
        },
      ],
      exampleQuery: '"native title rights" "connection to country" "traditional laws and customs"',
    },
    {
      id: 'wills-estates-succession',
      title: 'Wills, Estates & Succession Law',
      shortDesc: 'Will disputes, estate administration, testamentary capacity, and family provision claims.',
      icon: 'FaScroll',
      colour: '#7D6608',
      overview:
        'Succession lawyers work under state-based succession acts, trustee acts, and the growing body of case law on testamentary capacity, undue influence, and family provision claims. With Australia\u2019s ageing population and an estimated $3.5 trillion in intergenerational wealth transfer expected over the next two decades, contested estates are increasing significantly.',
      stats: [
        { value: '10,400+', label: 'Succession and estate law decisions indexed' },
        { value: '$3.5T', label: 'Estimated intergenerational wealth transfer over the next 20 years' },
        { value: '3.1x', label: 'Faster to research family provision claim precedents' },
      ],
      benefits: [
        {
          title: 'Family Provision Claims',
          description:
            'Search "family provision claim adequate provision maintenance support" and JurisTab returns state Supreme Court decisions. Brief extracts how courts have applied the two-stage test from Singer v Berghouse [1994] HCA 40 \u2014 assessing whether provision is inadequate, then determining what provision should be made.',
        },
        {
          title: 'Testamentary Capacity Disputes',
          description:
            'JurisLink traces the application of the Banks v Goodfellow [1870] test through modern Australian authorities. Track how courts have assessed capacity in the context of dementia, undue influence, and suspicious circumstances, including the evidentiary requirements for challenging a will.',
        },
        {
          title: 'Estate Administration & Trustee Duties',
          description:
            'JurisLink connects decisions on executor duties, trustee powers, estate accounting obligations, and the Benjamin order process. Research how courts have handled complex estate administration issues including digital assets, superannuation death benefits, and blended family distributions.',
        },
      ],
      exampleQuery: '"testamentary capacity" "suspicious circumstances" "knowledge and approval" will',
    },
  ],

  howItWorks: {
    label: 'How JurisTab Works for You',
    title: 'From Search to Strategy in Seconds',
    steps: [
      {
        title: 'Search in Plain English',
        description: 'Describe your legal issue in natural language. No need for Boolean operators or exact citation formats \u2014 JurisTab understands legal context.',
      },
      {
        title: 'Get Practice-Specific Results',
        description: 'Results are ranked by relevance to your practice area, with AI-powered summaries highlighting the key ratio, facts, and judicial reasoning.',
      },
      {
        title: 'Explore Connections',
        description: 'Use Reasoning, Appearances, and JurisLink to visualise how cases relate to each other, trace citation lineage, and discover precedents you might have missed.',
      },
      {
        title: 'Build Stronger Arguments',
        description: 'Export structured summaries, citation chains, and comparable decisions directly into your workflow. Spend less time searching, more time advising.',
      },
    ],
  },

  suiteLabel: 'Dedicated Workflow Services',
  suiteLink: '/workflow-features',
  suiteLinkText: 'See the suite on the Workflow page \u2192',

  cta: {
    title: 'Transform Your Practice Area Research Today',
    subtitle:
      'Join thousands of Australian lawyers who use JurisTab to find better precedents faster. Start your free trial and see the difference AI-powered research makes in your specific practice area.',
    ctaPrimary: 'Start Free Trial',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── Footer ── */

export const FOOTER = {
  tagline: SITE.tagline,
  productsHeading: 'Products',
  products: [
    { label: 'JurisTab', href: APP_URL, external: true },
    { label: 'DocuDive', href: DOCUDIVE_URL, external: true },
  ],
  companyHeading: 'Company',
  companyLinks: [
    { label: 'About Us', href: '/about' },
    { label: 'Case Management', href: '/case-management' },
    { label: 'Legal Research', href: '/features' },
    { label: 'Workflow', href: '/workflow-features' },
    { label: 'Practice Areas', href: '/practice-areas' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Blogs', href: '/blogs' },
    { label: 'Contact', href: '/contact' },
  ],
  supportHeading: 'Support',
  supportLinks: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms-and-conditions' },
  ],
  newsletter: {
    heading: 'Stay Updated',
    text: 'Subscribe to our newsletter for the latest legal-tech insights and product updates.',
    placeholder: 'Enter your email',
    button: 'Subscribe',
  },
  bottomLinks: [
    { label: 'Privacy', href: '/privacy-policy' },
    { label: 'Terms', href: '/terms-and-conditions' },
    { label: 'Contact', href: '/contact' },
  ],
  /* Accessible name of the country switcher beside the copyright. */
  marketSwitcherLabel: 'Choose your country',
};

/* ── Market notice ── */

/* Nobody is geo-redirected to the Australian site, so it has no notice. */
export const MARKET_NOTICE: MarketNotice | null = null;

/* ── Tools & Calculators ── */

/* The three modules JurisTab ships. A firm that will only use one of them
   should only be credited with that one's time saving, so these are toggles
   rather than fixed assumptions. Counts match the live billing catalogue. */
export const CALC_MODULES: { key: string; label: string; hint: string; info: string }[] = [
  {
    key: 'case_management',
    label: 'Case Management',
    hint: '9 capabilities — intake, matters, calendaring, time, billing, signing',
    info: 'Running the practice: taking on a client, opening and organising the matter file, diarising dates, capturing time and getting the bill out. Turn it off if you will keep using your existing practice-management system.',
  },
  {
    key: 'workflow',
    label: 'Workflow Automation',
    hint: '44 agentic services — review, redline, extract, draft, court forms',
    info: 'The document work: reading a contract for risk, pulling out clauses, dates and obligations, comparing versions, drafting and organising bundles. This is the biggest single slice of time for most firms.',
  },
  {
    key: 'legal_research',
    label: 'Legal Research',
    hint: '5 research actions — search, case unlock, legislation, JurisBot',
    info: 'Finding and digesting authority: searching Australian case law and legislation, opening a judgment, and asking JurisBot questions about it. The most compressible work of the three, because reading is what AI shortens most.',
  },
];

/* Plan options for the cost side. 'auto' hands the choice to the backend,
   which prices every plan at your volume and picks the cheapest — the same
   comparison your in-app dashboard runs each month. */
export const CALC_PLANS: { key: string; label: string; info: string }[] = [
  { key: 'auto', label: 'Cheapest for me', info: 'We price all five plans at the volumes you entered and cost the report on whichever comes out cheapest. This is the default and usually the right answer.' },
  { key: 'pay_as_you_go', label: 'Pay as you go', info: 'No subscription at all — every page, case unlock and JurisBot message is paid for in credits as you use it. Cheapest for light or occasional use.' },
  { key: 'pro_case_management', label: 'Case Management', info: `${money(59)} per user per month. Unlimited case management — matters, contacts, calendars, time and billing. Document and research usage is still metered.` },
  { key: 'pro_research', label: 'Legal Research', info: `${money(159)} per user per month. Unlimited research — case unlocks, legislation and JurisBot. Document pages are still metered.` },
  { key: 'pro_workflow', label: 'Workflow', info: `${money(169)} per user per month, including 1,200 processed pages each month. Pages past that are charged at the plan rate.` },
  { key: 'pro_plus', label: 'Pro+', info: `${money(299)} per user per month. Everything unlimited except pages, of which 3,000 a month are included, with 25% rollover. The usual answer for a team using all three modules.` },
];

/* The two sensitivity knobs and the safety margin, defined in plain English so
   the advanced panel can explain itself. `default` marks the value the
   calculator starts on. */
export const CALC_SCENARIOS: { key: string; label: string; info: string; default?: boolean }[] = [
  { key: 'conservative', label: 'Conservative', info: 'Assumes each module gives back 70% of the time our model says it should. Use this when you want the number to survive a sceptical CFO.' },
  { key: 'expected', label: 'Expected', info: 'The model as measured — the middle case, and where the calculator starts.', default: true },
  { key: 'optimistic', label: 'Optimistic', info: 'Assumes 25% more time back than the model says — a well-run rollout with a team that adopts the tools properly.' },
];

export const CALC_ADOPTIONS: { key: 3 | 6 | 12; label: string; info: string; default?: boolean }[] = [
  { key: 3, label: '3 mo', info: 'A fast rollout: usage climbs to full over the first 3 months, so Year 1 captures 87.5% of the full benefit. Years 2 and 3 get all of it.' },
  { key: 6, label: '6 mo', info: 'A typical rollout — usage climbs to full over 6 months, so Year 1 captures 75% of the full benefit and Years 2 and 3 get all of it. This is where the calculator starts.', default: true },
  { key: 12, label: '12 mo', info: 'A cautious rollout across a large or busy team: usage takes the whole first year to reach full, so Year 1 captures 50% of the benefit. Years 2 and 3 get all of it.' },
];

/* A flat haircut taken off the savings — and never off the cost — before
   anything is shown. Separate from the confidence scenario: that one asks how
   much time comes back, this one is a margin of safety on the finished number.
   15% is the default, and matches `prudence_buffer` in the backend config. */
export const CALC_BUFFERS: { key: number; label: string; info: string; default?: boolean }[] = [
  { key: 0, label: 'None', info: 'Show the model exactly as it computes, with no safety margin. Useful to see the raw arithmetic; not what we would put in front of a board.' },
  { key: 0.1, label: '10%', info: 'A light safety margin: you are shown 90% of the savings the model works out.' },
  { key: 0.15, label: '15%', info: 'The default. You are shown 85% of the savings the model works out — the other 15% is held back for everything a model cannot see: the matters that do not compress, the weeks nobody logs in, the assumption that turns out generous.', default: true },
  { key: 0.25, label: '25%', info: 'A heavy safety margin: you are shown 75% of the savings. Pair it with the conservative scenario for a genuine worst case.' },
];

export const CALCULATORS_HERO = {
  label: 'Tools & Calculators',
  title: 'Quantify your return on JurisTab',
  subtitle:
    'JurisTab’s 32 capabilities span three modules — case management, workflow automation and legal research — and each gives back a different slice of the week. These calculators show where you stand today, price the regained capacity against the plan you would actually be on, and tell you which plan that is.',
  /* The "Modules you will use" control in the advanced panel. */
  modulesInfo:
    'JurisTab is three products in one. Each gives back a different slice of the week, so only the ones you switch on are counted towards your saving. All three are on by default.',
  modulesHint:
    'Each module gives back a different slice of the week, so only the ones you turn on are counted. Default: all three.',
};

/* Shared definitions — the cost side is identical for every profession, so the
   wording of it should be too. */
const COST_ROWS = (who: string, group = 'What JurisTab costs'): CalcRow[] => [
  {
    key: 'subscription_cost',
    label: 'JurisTab Subscription',
    format: 'money',
    group,
    info: `A seat for each of your ${who}, on the plan shown above, at the annual price — pay for ten months and get twelve. This is the fixed part of the bill.`,
  },
  {
    key: 'usage_cost',
    label: 'JurisTab Metered Usage',
    format: 'money',
    group,
    info: 'The variable part of the bill: document pages beyond what your plan includes, plus any research or contact actions your plan does not make unlimited. Uploading, OCR, search, matters, tasks and exports are free on every plan.',
  },
  {
    key: 'cost_of_juristab',
    label: 'Total Cost of JurisTab',
    format: 'money',
    group,
    info: 'Subscription plus metered usage — the whole JurisTab bill for the year, with nothing held back. Today it is zero, because you are not paying it yet.',
  },
];

export const CALCULATORS: Record<string, CalcProfession> = {
  'in-house': {
    slug: 'in-house',
    service: 'in-house',
    navLabel: 'In-House Lawyers',
    badge: 'In-House / Corporate Legal',
    title: 'In-House Legal ROI Calculator',
    subtitle:
      'In-house teams don’t bill by the hour, so value shows up as freed capacity (deferring the next hire) and outside-counsel spend brought back in-house. Every figure is shown against where your team stands today.',
    formTitle: 'Your Team',
    fields: [
      {
        name: 'num_lawyers',
        label: 'Number of Lawyers',
        type: 'number',
        default: 20,
        min: 1,
        step: 1,
        info: 'How many qualified lawyers sit in your in-house team. Count anyone who would use JurisTab — paralegals and legal ops included — since they all need a seat and they all get time back.',
      },
      {
        name: 'practice_areas',
        label: 'Practice Areas',
        type: 'number',
        default: 1,
        min: 1,
        step: 1,
        info: `How many practice areas you will subscribe to. The quoted price covers one; each extra area adds 33% of it on Workflow, Legal Research and Pro+ (Pro+ at 3 areas is about ${money(496)} per user per month), and 33% on metered usage on Pay as you go. Case Management is one price however many areas you run.`,
      },
      {
        name: 'business_growth_percent',
        label: 'Business Growth (% per year)',
        type: 'number',
        default: 5,
        min: 0,
        step: 0.5,
        hint: 'Year 1 is today’s volume; Years 2 and 3 grow by this much each.',
        info: 'How much your legal workload grows each year — more matters, more contracts, a bigger outside-counsel bill. Year 1 is today’s volume; Years 2 and 3 grow by this much each, so there is more work for JurisTab to speed up and more external work to bring in-house. Your JurisTab subscription does not grow with it. 5% is a typical figure; set 0 to model no growth.',
      },
      {
        name: 'avg_loaded_salary',
        label: 'Average Lawyer Salary',
        type: 'currency',
        default: 200000,
        min: 0,
        step: 1000,
        hint: 'Annual base salary; we apply a loaded-cost multiplier for benefits & overhead.',
        info: 'The average base salary across the team, before super, bonuses or overhead. We multiply it by 1.3 to get the true cost of employing someone, which is what an hour of their time is actually worth to the business.',
      },
      {
        name: 'outside_counsel_spend',
        label: 'Annual Outside Counsel Spend',
        type: 'currency',
        default: 1000000,
        min: 0,
        step: 10000,
        info: 'What you pay external law firms in a year, across all panel firms and one-off instructions. With more internal capacity, a slice of this work comes back in-house — that slice is a direct cash saving.',
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'currency-select',
        default: REGION.currency,
        info: 'The currency your own figures are in, and the one results are shown in. JurisTab is sold in Australian dollars; other currencies convert the published AUD price at an indicative rate, and you are still billed in AUD.',
      },
    ],
    usageFields: [
      {
        name: 'pages_per_month',
        label: 'Pages Processed / Lawyer / Month',
        type: 'number',
        default: 300,
        min: 0,
        step: 10,
        hint: 'Pages a workflow service actually reads. Upload, OCR and export are never counted.',
        info: 'Pages an AI service actually reads for you in a month — a contract you send for risk review, a lease you pull key dates out of. Just storing, uploading, OCR-ing or exporting a document costs nothing, so those pages do not count here.',
      },
      {
        name: 'case_unlocks_per_month',
        label: 'Case Unlocks / Lawyer / Month',
        type: 'number',
        default: 5,
        min: 0,
        step: 1,
        info: 'How many times a lawyer opens the full text of a court judgment in a month. Searching for cases is free and unlimited; you only pay when you open one to read it.',
      },
      {
        name: 'legislation_unlocks_per_month',
        label: 'Legislation Sections / Lawyer / Month',
        type: 'number',
        default: 10,
        min: 0,
        step: 1,
        info: 'How many sections of an Act or regulation a lawyer opens in full in a month — the Corporations Act section you need to quote exactly, not the search that found it.',
      },
      {
        name: 'jurisbot_messages_per_month',
        label: 'JurisBot Messages / Lawyer / Month',
        type: 'number',
        default: 25,
        min: 0,
        step: 5,
        info: 'Questions asked of JurisBot, our legal assistant, in a month — one message is one question and its answer.',
      },
      {
        name: 'new_contacts_per_month',
        label: 'New Contact Records / Lawyer / Month',
        type: 'number',
        default: 3,
        min: 0,
        step: 1,
        info: 'New people or organisations added to the contact book each month. Only creating a brand-new record is metered — editing, linking and matter work are all free.',
      },
    ],
    team: {
      headcountField: 'num_lawyers',
      rateField: 'avg_loaded_salary',
      rateLabel: 'Annual salary',
      rateKind: 'salary',
      roles: ['General counsel', 'Senior counsel', 'Lawyer', 'Paralegal', 'Legal ops'],
      info: 'Enter the team as it really is — full-timers and part-timers, each on their own salary. Everyone who uses JurisTab needs a seat, so every person is priced; the time given back and what an hour is worth follow how many days each person actually works. For a part-timer, enter what you actually pay them.',
    },
    headlineKey: 'annual_roi_multiple',
    headlineLabel: 'Annual Return on Investment',
    headlineInfo:
      'For every dollar you spend on JurisTab in Year 1, this is the value that comes back — freed capacity plus outside-counsel savings, divided by the total bill. 3× means three dollars back for every one spent.',
    baselineLabel: 'Today',
    baselineInfo:
      'Where your legal function stands right now, before JurisTab: what the team costs, what you send out to external firms, and how much of the year goes into work the modules you selected can help with. Every later column is measured against this one.',
    supportMetrics: [
      {
        key: 'year_1_net_benefit',
        label: 'Year 1 Net Benefit',
        format: 'money',
        info: 'Value created in the first year — freed capacity plus outside-counsel savings — after the whole JurisTab bill has been taken off.',
      },
      {
        key: 'fte_equivalent_year_1',
        label: 'FTE Capacity Regained',
        format: 'number',
        info: 'The hours given back, expressed as full-time lawyers. 2.5 means you get the working year of two and a half people back without hiring anyone.',
      },
      {
        key: 'payback_period_months',
        label: 'Payback Period',
        format: 'months',
        info: 'How long it takes for the value created to cover the annual cost of JurisTab. Under twelve months means it pays for itself inside the first year.',
      },
    ],
    rows: [
      {
        key: 'hours_on_addressable_work',
        label: 'Hours on Work JurisTab Touches',
        format: 'hours',
        group: 'Where the team’s time goes',
        info: 'Hours a year your team currently spends on the work the modules you selected can help with — file admin, document work and research. This is the pool the saving comes out of, not your whole working year.',
      },
      {
        key: 'time_regained_hours',
        label: 'Time Regained (Hours)',
        format: 'hours',
        group: 'Where the team’s time goes',
        info: 'Hours a year handed back to the team, after the safety margin and the adoption ramp. Today it is zero, because none of this work is automated yet.',
      },
      {
        key: 'fte_equivalent',
        label: 'FTE-Equivalent Capacity',
        format: 'number',
        group: 'Where the team’s time goes',
        info: 'Those regained hours expressed as full-time lawyers — the headcount you would otherwise have to hire to get the same capacity.',
      },
      {
        key: 'internal_team_cost',
        label: 'In-House Team Cost',
        format: 'money',
        group: 'Cash out the door',
        info: 'What your lawyers cost the business a year: salaries multiplied by 1.3 for super, benefits, tax and overhead. This does not change — the model never assumes anyone is let go.',
      },
      {
        key: 'outside_counsel_spend',
        label: 'Outside Counsel Spend',
        format: 'money',
        group: 'Cash out the door',
        info: 'Your annual bill from external law firms. It falls each year as freed capacity lets the team keep more of that work in-house.',
      },
      ...COST_ROWS('lawyers', 'Cash out the door'),
      {
        key: 'total_run_rate',
        label: 'Total Legal Run-Rate',
        format: 'money',
        group: 'Cash out the door',
        info: 'Real money leaving the business each year: your team, your external firms and JurisTab added together. Freed capacity is deliberately left out of this line — it is value, not a cheque anyone stops writing.',
      },
      {
        key: 'freed_capacity_value',
        label: 'Freed Capacity Value',
        format: 'money',
        group: 'Value created',
        info: 'The regained hours priced at what your team actually costs per hour. It is the value of the hire you do not have to make — real, but it shows up as work absorbed rather than as cash in the bank.',
      },
      {
        key: 'outside_counsel_savings',
        label: 'Outside Counsel Savings',
        format: 'money',
        group: 'Value created',
        info: 'External legal spend you stop paying because the freed capacity lets you handle that work internally. Unlike freed capacity, this one is cash you genuinely keep.',
      },
      {
        key: 'potential_additional_profit',
        label: 'Net Annual Benefit',
        format: 'money',
        group: 'The bottom line',
        emphasis: true,
        info: 'Freed capacity plus outside-counsel savings, minus everything JurisTab costs. The single number to take to a budget meeting.',
      },
      {
        key: 'profit_increase_percent',
        label: 'Increase vs Today',
        format: 'percent',
        group: 'The bottom line',
        info: 'Net Annual Benefit as a share of what the legal function costs today — the in-house team’s loaded cost plus outside-counsel spend. In-house teams have no profit line, so this is the saving measured against your current legal budget.',
      },
    ],
  },

  'private-practice': {
    slug: 'private-practice',
    service: 'private-practice',
    navLabel: 'Private Practice',
    badge: 'Private Practice / Law Firm',
    title: 'Private Practice ROI Calculator',
    subtitle:
      'For fee earners, regained hours become incremental billable capacity — valued at your rate and discounted by realization and collection, the two classic law-firm leakage metrics. Shown against the fees you already collect today.',
    formTitle: 'Your Firm',
    fields: [
      {
        name: 'num_lawyers',
        label: 'Number of Lawyers',
        type: 'number',
        default: 300,
        min: 1,
        step: 1,
        info: 'How many fee earners you have — everyone who records billable time and would use JurisTab. Support staff who never bill are best left out.',
      },
      {
        name: 'practice_areas',
        label: 'Practice Areas',
        type: 'number',
        default: 1,
        min: 1,
        step: 1,
        info: `How many practice areas you will subscribe to. The quoted price covers one; each extra area adds 33% of it on Workflow, Legal Research and Pro+ (Pro+ at 3 areas is about ${money(496)} per user per month), and 33% on metered usage on Pay as you go. Case Management is one price however many areas you run.`,
      },
      {
        name: 'business_growth_percent',
        label: 'Business Growth (% per year)',
        type: 'number',
        default: 5,
        min: 0,
        step: 0.5,
        hint: 'Year 1 is today’s volume; Years 2 and 3 grow by this much each.',
        info: 'How much the firm’s work grows each year — more matters, more documents, more fees. Year 1 is today’s volume; Years 2 and 3 grow by this much each, so there is more work for JurisTab to speed up. Your JurisTab subscription does not grow with it. Australian legal services have grown around 4–6% a year; set 0 to model no growth.',
      },
      {
        name: 'hourly_rate',
        label: 'Average Hourly Rate',
        type: 'currency',
        default: 700,
        min: 0,
        step: 10,
        hint: 'Your average billable rate per hour.',
        info: 'The blended rate you charge across the firm — partners, senior associates and juniors averaged together, not the partner rate. Every regained hour is valued at this.',
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'currency-select',
        default: REGION.currency,
        info: 'The currency your own figures are in, and the one results are shown in. JurisTab is sold in Australian dollars; other currencies convert the published AUD price at an indicative rate, and you are still billed in AUD.',
      },
    ],
    usageFields: [
      {
        name: 'pages_per_month',
        label: 'Pages Processed / Lawyer / Month',
        type: 'number',
        default: 400,
        min: 0,
        step: 10,
        hint: 'Pages a workflow service actually reads. Upload, OCR and export are never counted.',
        info: 'Pages an AI service actually reads for you in a month — a contract you send for risk review, a bundle you have summarised. Just storing, uploading, OCR-ing or exporting a document costs nothing, so those pages do not count here.',
      },
      {
        name: 'case_unlocks_per_month',
        label: 'Case Unlocks / Lawyer / Month',
        type: 'number',
        default: 12,
        min: 0,
        step: 1,
        info: 'How many times a fee earner opens the full text of a court judgment in a month. Searching for cases is free and unlimited; you only pay when you open one to read it.',
      },
      {
        name: 'legislation_unlocks_per_month',
        label: 'Legislation Sections / Lawyer / Month',
        type: 'number',
        default: 8,
        min: 0,
        step: 1,
        info: 'How many sections of an Act or regulation a fee earner opens in full in a month — the provision you need to quote exactly, not the search that found it.',
      },
      {
        name: 'jurisbot_messages_per_month',
        label: 'JurisBot Messages / Lawyer / Month',
        type: 'number',
        default: 30,
        min: 0,
        step: 5,
        info: 'Questions asked of JurisBot, our legal assistant, in a month — one message is one question and its answer.',
      },
      {
        name: 'new_contacts_per_month',
        label: 'New Contact Records / Lawyer / Month',
        type: 'number',
        default: 5,
        min: 0,
        step: 1,
        info: 'New clients and other parties added to the contact book each month. Only creating a brand-new record is metered — editing, linking and matter work are all free.',
      },
    ],
    team: {
      headcountField: 'num_lawyers',
      rateField: 'hourly_rate',
      rateLabel: 'Hourly rate',
      rateKind: 'hourly',
      roles: ['Partner', 'Senior associate', 'Solicitor', 'Law clerk', 'Paralegal'],
      info: 'Enter the firm as it really is — solicitors, clerks and paralegals, full- or part-time, each at their own charge-out rate. Everyone who uses JurisTab needs a seat, so every person is priced; the time given back follows how many days each person actually works, and each hour is valued at that person’s rate. Put 0 for anyone whose time is not billed.',
    },
    headlineKey: 'annual_roi_multiple',
    headlineLabel: 'Annual Return on Investment',
    headlineInfo:
      'For every dollar you spend on JurisTab in Year 1, this is the fee value that comes back. 3× means three dollars of collected fees for every one spent.',
    baselineLabel: 'Today',
    baselineInfo:
      'Where your firm stands right now, before JurisTab: the hours currently going into work the selected modules can help with, and the fees those hours actually collect. Every later column is measured against this one.',
    supportMetrics: [
      {
        key: 'year_1_additional_profit',
        label: 'Year 1 Additional Profit',
        format: 'money',
        info: 'Extra fees collected in the first year from the freed capacity, after the whole JurisTab bill has been taken off.',
      },
      {
        key: 'three_year_additional_profit',
        label: '3-Year Additional Profit',
        format: 'money',
        info: 'The same figure added up across all three years, as the team climbs the adoption curve and the benefit reaches full strength.',
      },
      {
        key: 'payback_period_months',
        label: 'Payback Period',
        format: 'months',
        info: 'How long it takes for the extra fees to cover the annual cost of JurisTab. Under twelve months means it pays for itself inside the first year.',
      },
    ],
    rows: [
      {
        key: 'hours_on_addressable_work',
        label: 'Hours on Work JurisTab Touches',
        format: 'hours',
        group: 'Where the firm’s time goes',
        info: 'Hours a year your fee earners currently spend on the work the modules you selected can help with — file admin, document work and research. This is the pool the saving comes out of, not the whole working year.',
      },
      {
        key: 'time_regained_hours',
        label: 'Time Regained (Hours)',
        format: 'hours',
        group: 'Where the firm’s time goes',
        info: 'Hours a year handed back to fee earners, after the safety margin and the adoption ramp. Today it is zero, because none of this work is automated yet.',
      },
      {
        key: 'billable_hours_converted',
        label: 'Hours Converted to Chargeable Work',
        format: 'hours',
        group: 'Where the firm’s time goes',
        info: 'Only the share of freed hours you can actually fill with chargeable work. We assume half — freeing time is not the same as selling it, and a firm that frees a fifth of its year cannot usually bill a fifth more.',
      },
      {
        key: 'fees_collected',
        label: 'Fees Collected',
        format: 'money',
        group: 'Fees',
        info:
          'What clients actually pay the firm in a year — less than what lawyers charge:\n' +
          `• Hours worked × hourly rate = value of time recorded (e.g. 4 × 1,800 × ${money(290)} = ${money(2088000)})\n` +
          `• × 85% realisation: some recorded time is written off or discounted and never invoiced (= ${money(1774800)} invoiced)\n` +
          `• × 90% collection: some invoices are never paid — bad debt, disputes (= ${money(1597320)} collected)\n\n` +
          'It assumes each full-time lawyer works a full 1,800-hour year, so it is a capacity figure, not your books. Each later year grows by your Business Growth rate and adds that year’s Net Incremental Billable Capacity.',
      },
      {
        key: 'net_incremental_billable_capacity',
        label: 'Net Incremental Billable Capacity',
        format: 'money',
        group: 'Fees',
        info: 'The extra fees on their own: hours regained × 50% that turn into chargeable work × your hourly rate × 85% realization × 90% collection. Year 1 is lower while the team ramps up (75% of the full saving with the default 6-month rollout; the team is at full use from then on). Years 2 and 3 rise only with your Business Growth rate — at 0% they are the same.',
      },
      ...COST_ROWS('fee earners'),
      {
        key: 'potential_additional_profit',
        label: 'Potential Additional Profit',
        format: 'money',
        group: 'The bottom line',
        emphasis: true,
        info: 'The extra fees collected minus everything JurisTab costs. The single number to take to a partners’ meeting.',
      },
      {
        key: 'profit_increase_percent',
        label: 'Profit Increase (% of Today’s Fees)',
        format: 'percent',
        group: 'The bottom line',
        info: 'Potential Additional Profit as a share of today’s Fees Collected. The calculator does not know your current profit, so this is measured against revenue: 5% means extra profit equal to 5% of what the firm collects today. Measured against profit alone, the increase would be several times larger.',
      },
    ],
  },

  conveyancers: {
    slug: 'conveyancers',
    service: 'conveyancer',
    navLabel: 'Conveyancers',
    badge: 'Conveyancing Practice',
    title: 'Conveyancer ROI Calculator',
    subtitle:
      'Conveyancers charge a fixed fee per matter, so value is about throughput: faster contract, title, search and file work frees capacity for additional settlements at the same headcount. Shown against the matters you already settle today.',
    formTitle: 'Your Practice',
    fields: [
      {
        name: 'num_conveyancers',
        label: 'Number of Conveyancers',
        type: 'number',
        default: 8,
        min: 1,
        step: 1,
        info: 'How many people run matters end to end — licensed conveyancers and the paralegals who carry their own files. Everyone who would need a seat.',
      },
      {
        name: 'practice_areas',
        label: 'Practice Areas',
        type: 'number',
        default: 1,
        min: 1,
        step: 1,
        info: `How many practice areas you will subscribe to. The quoted price covers one; each extra area adds 33% of it on Workflow, Legal Research and Pro+ (Pro+ at 3 areas is about ${money(496)} per user per month), and 33% on metered usage on Pay as you go. Case Management is one price however many areas you run.`,
      },
      {
        name: 'business_growth_percent',
        label: 'Business Growth (% per year)',
        type: 'number',
        default: 5,
        min: 0,
        step: 0.5,
        hint: 'Year 1 is today’s volume; Years 2 and 3 grow by this much each.',
        info: 'How much your matter volume grows each year. Year 1 is today’s volume; Years 2 and 3 grow by this much each, so there are more matters for JurisTab to speed up. Your JurisTab subscription does not grow with it. 5% is a typical figure; set 0 to model no growth.',
      },
      {
        name: 'matters_per_month',
        label: 'Matters per Conveyancer / Month',
        type: 'number',
        default: 18,
        min: 1,
        step: 1,
        info: 'Settlements one conveyancer completes in an average month. Count the matter once, whichever side you act for.',
      },
      {
        name: 'fee_per_matter',
        label: 'Average Fee per Matter',
        type: 'currency',
        default: 1200,
        min: 0,
        step: 50,
        info: 'Your professional fee for a standard matter, excluding disbursements and search fees you pass straight through at cost.',
      },
      {
        name: 'hours_per_matter',
        label: 'Hours per Matter',
        type: 'number',
        default: 6,
        min: 1,
        step: 1,
        info: 'Hands-on hours a matter takes from opening to settlement. It is what sets your ceiling: cut the hours and the same team can settle more.',
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'currency-select',
        default: REGION.currency,
        info: 'The currency your own figures are in, and the one results are shown in. JurisTab is sold in Australian dollars; other currencies convert the published AUD price at an indicative rate, and you are still billed in AUD.',
      },
    ],
    /* Pages and new contacts are derived from matter volume by the backend —
       a conveyancing matter carries a known bundle and (mostly) a new client —
       so only the research figures are asked for here. */
    usageFields: [
      {
        name: 'case_unlocks_per_month',
        label: 'Case Unlocks / Conveyancer / Month',
        type: 'number',
        default: 1,
        min: 0,
        step: 1,
        info: 'How many times you open the full text of a court judgment in a month. Searching is free and unlimited; you only pay when you open one to read it. Rare in conveyancing, hence the low default.',
      },
      {
        name: 'legislation_unlocks_per_month',
        label: 'Legislation Sections / Conveyancer / Month',
        type: 'number',
        default: 3,
        min: 0,
        step: 1,
        info: 'Sections of an Act or regulation you open in full each month — a vendor-disclosure provision you need to quote exactly, for instance.',
      },
      {
        name: 'jurisbot_messages_per_month',
        label: 'JurisBot Messages / Conveyancer / Month',
        type: 'number',
        default: 8,
        min: 0,
        step: 1,
        info: 'Questions asked of JurisBot, our legal assistant, in a month — one message is one question and its answer.',
      },
    ],
    team: {
      headcountField: 'num_conveyancers',
      roles: ['Licensed conveyancer', 'Paralegal', 'Clerk'],
      info: 'Enter the team as it really is — full- and part-time. Everyone who uses JurisTab needs a seat, so every person is priced; matter volume and time given back follow how many days each person actually works.',
    },
    headlineKey: 'annual_roi_multiple',
    headlineLabel: 'Annual Return on Investment',
    headlineInfo:
      'For every dollar you spend on JurisTab in Year 1, this is the fee contribution that comes back from the extra settlements. 3× means three dollars back for every one spent.',
    baselineLabel: 'Today',
    baselineInfo:
      'Where your practice stands right now, before JurisTab: the settlements you complete in a year, the hours going into the parts of them the selected modules can help with, and what those matters contribute. Every later column is measured against this one.',
    supportMetrics: [
      {
        key: 'year_1_additional_profit',
        label: 'Year 1 Additional Profit',
        format: 'money',
        info: 'Contribution from the extra settlements in the first year, after the whole JurisTab bill has been taken off.',
      },
      {
        key: 'additional_matters_year_1',
        label: 'Extra Matters / Year',
        format: 'number',
        info: 'How many additional settlements the same team can complete in Year 1 with the time the tools give back.',
      },
      {
        key: 'payback_period_months',
        label: 'Payback Period',
        format: 'months',
        info: 'How long it takes for the extra contribution to cover the annual cost of JurisTab. Under twelve months means it pays for itself inside the first year.',
      },
    ],
    rows: [
      {
        key: 'hours_on_addressable_work',
        label: 'Hours on Work JurisTab Touches',
        format: 'hours',
        group: 'Where the practice’s time goes',
        info: 'Hours a year currently going into the parts of a matter the modules you selected can help with — the contract and disclosure review, title and search summaries, and the file itself. Not your whole year on matters.',
      },
      {
        key: 'time_regained_hours',
        label: 'Time Regained (Hours)',
        format: 'hours',
        group: 'Where the practice’s time goes',
        info: 'Hours a year handed back across all your matters, after the safety margin and the adoption ramp. Today it is zero, because none of this work is automated yet.',
      },
      {
        key: 'annual_matters',
        label: 'Matters Settled per Year',
        format: 'number',
        group: 'Throughput',
        info: 'Settlements completed in a year. "Today" is your current throughput; the later columns add the extra matters the freed time makes room for, at the same headcount.',
      },
      {
        key: 'additional_matter_capacity',
        label: 'Additional Matter Capacity',
        format: 'number',
        group: 'Throughput',
        info: 'The extra settlements on their own. We only count half the freed time as new matters — the work has to exist to fill it, and a quiet market will not hand you more files just because you have room.',
      },
      {
        key: 'annual_contribution',
        label: 'Fee Contribution per Year',
        format: 'money',
        group: 'Value created',
        info: 'Fee income from those matters after the variable cost of running them — the money left over to cover the practice and its profit. "Today" is your current position.',
      },
      {
        key: 'additional_revenue',
        label: 'Additional Contribution',
        format: 'money',
        group: 'Value created',
        info: 'The uplift on its own: fee contribution from the extra settlements, net of what those extra matters cost to run.',
      },
      ...COST_ROWS('conveyancers'),
      {
        key: 'potential_additional_profit',
        label: 'Potential Additional Profit',
        format: 'money',
        group: 'The bottom line',
        emphasis: true,
        info: 'The extra contribution minus everything JurisTab costs. The single number to take to a planning meeting.',
      },
      {
        key: 'profit_increase_percent',
        label: 'Profit Increase (% of Today)',
        format: 'percent',
        group: 'The bottom line',
        info: 'Potential Additional Profit as a share of today’s Annual Contribution — the profit your matters earn now. 5% means 5% more than today.',
      },
    ],
  },
};
